import { useState, useEffect, useCallback, useRef } from "react";

// ── API config ────────────────────────────────────────────────────────
const API_URL    = "/api.php";
const UPLOAD_URL = "/upload.php";

// ── Auth ──────────────────────────────────────────────────────────────
const ADMIN_PASSWORD  = "uniportal@admin";
const PORTAL_PASSWORD = "uniportal@2026";

// ── Course colours ────────────────────────────────────────────────────
const TAG_PALETTE = [
  {bg:"#dbeafe",color:"#1d4ed8"},{bg:"#fef3c7",color:"#b45309"},
  {bg:"#fce7f3",color:"#be185d"},{bg:"#f3e8ff",color:"#7c3aed"},
  {bg:"#ecfdf5",color:"#065f46"},{bg:"#fff7ed",color:"#c2410c"},
  {bg:"#f0fdf4",color:"#166534"},{bg:"#fdf4ff",color:"#86198f"},
  {bg:"#ffe4e6",color:"#9f1239"},{bg:"#f1f5f9",color:"#475569"},
  {bg:"#fef9c3",color:"#a16207"},{bg:"#e0f2fe",color:"#0369a1"},
];

const DEFAULT_COURSES = [
  { value:"mbbs",        label:"MBBS",                 color:"#1d4ed8", bg:"#dbeafe" },
  { value:"mba",         label:"MBA",                  color:"#b45309", bg:"#fef3c7" },
  { value:"nursing",     label:"Nursing / B.S Nursing",color:"#be185d", bg:"#fce7f3" },
  { value:"bba",         label:"BBA",                  color:"#7c3aed", bg:"#f3e8ff" },
  { value:"pharmacy",    label:"Pharmacy",             color:"#065f46", bg:"#ecfdf5" },
  { value:"allied",      label:"Allied Health",        color:"#c2410c", bg:"#fff7ed" },
  { value:"engineering", label:"Engineering",          color:"#166534", bg:"#f0fdf4" },
  { value:"arts",        label:"Arts",                 color:"#86198f", bg:"#fdf4ff" },
  { value:"bpt",         label:"BPT / BOT",            color:"#9f1239", bg:"#ffe4e6" },
  { value:"multi",       label:"Multiple Courses",     color:"#475569", bg:"#f1f5f9" },
];

const CLBL_STATIC = {
  mbbs:"MBBS", mba:"MBA", nursing:"Nursing", bba:"BBA",
  pharmacy:"Pharmacy", allied:"Allied", engineering:"Engineering",
  arts:"Arts", bpt:"BPT/BOT", multi:"Multiple",
};

function buildCLBL(courseList) {
  const m = {};
  courseList.forEach(c => { m[c.value] = c.label.split("/")[0].trim(); });
  return m;
}
function getCourseStyle(courseList, value) {
  const c = courseList.find(x => x.value === value);
  return c ? { background: c.bg, color: c.color } : { background:"#f1f5f9", color:"#475569" };
}
const CAT_LBL   = { primary:"Primary", secondary:"Secondary", premium:"Premium" };
const CAT_CLASS = { primary:"cat-primary", secondary:"cat-secondary", premium:"cat-premium" };
const CLBL = CLBL_STATIC;
const PH = "https://images.unsplash.com/photo-1523050853051-f050540c4419?auto=format&fit=crop&w=800&q=80";

const mkFee = (course, label, years = [], currency = "INR", feeMode = "year") => ({
  course, label, years, totalFee: "", brochureLink: "#", currency, feeMode,
  seats: "",
  hostelRows: [],
});

const FEE_MODES = [
  { value: "simple",   label: "Simple Total",   icon: "💵" },
  { value: "year",     label: "Year-wise",       icon: "📅" },
  { value: "semester", label: "Semester-wise",   icon: "📆" },
  { value: "term",     label: "Term-wise",       icon: "🗓️"  },
];

const CURRENCY_SYM = { INR: "₹", USD: "$" };

function fmtAmount(amount, currency = "INR") {
  return `${CURRENCY_SYM[currency] || "₹"} ${amount}`;
}

function calcTotalAmt(years) {
  const nums = years.map(y => parseInt((y.amount || "").replace(/[^0-9]/g,""))).filter(n => !isNaN(n));
  if (!nums.length) return null;
  return nums.reduce((a,b) => a+b, 0).toLocaleString("en-IN");
}

function calcHostelRowTotal(row) {
  const amt = parseInt((row.amount || "").replace(/[^0-9]/g, ""));
  const mul = parseInt(row.multiplier || 1);
  if (isNaN(amt)) return null;
  return (amt * mul).toLocaleString("en-IN");
}

const BLANK_CONTACT = { name: "", phone: "", email: "", whatsapp: "" };

const DEFAULT_ABROAD = [
  { id:"a1", type:"abroad", name:"Tajik National University", location:"Dushanbe, Tajikistan", country:"Tajikistan", courses:["mbbs"], courseLabel:"MBBS", seats:"—", hidden:false, images:["https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=800&q=80","https://images.unsplash.com/photo-1541339907198-e08756ebafe3?auto=format&fit=crop&w=800&q=80"], videos:[], brochure:"#", contact: { ...BLANK_CONTACT },
    feeStructures:[mkFee("mbbs","MBBS",[{label:"Year 1",amount:"3,50,000"},{label:"Year 2",amount:"3,20,000"},{label:"Year 3",amount:"3,20,000"},{label:"Year 4",amount:"3,00,000"},{label:"Year 5",amount:"3,00,000"},{label:"Internship",amount:"1,50,000"}])]
  },
  { id:"a2", type:"abroad", name:"Andijan State Medical University", location:"Andijan, Uzbekistan", country:"Uzbekistan", courses:["mbbs"], courseLabel:"MBBS", seats:"—", hidden:false, images:["https://images.unsplash.com/photo-1551190822-a9333d879b1f?auto=format&fit=crop&w=800&q=80","https://images.unsplash.com/photo-1568667256549-094345857637?auto=format&fit=crop&w=800&q=80"], videos:[], brochure:"#", contact: { ...BLANK_CONTACT },
    feeStructures:[mkFee("mbbs","MBBS",[{label:"Year 1",amount:"2,80,000"},{label:"Year 2",amount:"2,60,000"},{label:"Year 3",amount:"2,60,000"},{label:"Year 4",amount:"2,40,000"},{label:"Year 5",amount:"2,40,000"},{label:"Internship",amount:"1,20,000"}])]
  },
  { id:"a3", type:"abroad", name:"East European University", location:"Tbilisi, Georgia", country:"Georgia", courses:["mbbs","mba","nursing","bba"], courseLabel:"MBBS · MBA · B.S Nursing · BBA", seats:"—", hidden:false, images:["https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=800&q=80","https://images.unsplash.com/photo-1547448415-e9f5b28e570d?auto=format&fit=crop&w=800&q=80"], videos:[], brochure:"#", contact: { ...BLANK_CONTACT },
    feeStructures:[
      mkFee("mbbs","MBBS",[{label:"Year 1",amount:"4,00,000"},{label:"Year 2",amount:"3,80,000"},{label:"Year 3",amount:"3,80,000"},{label:"Year 4",amount:"3,50,000"},{label:"Year 5",amount:"3,50,000"},{label:"Year 6",amount:"3,20,000"}]),
      mkFee("mba","MBA",[{label:"Year 1",amount:"2,50,000"},{label:"Year 2",amount:"2,50,000"}]),
      mkFee("nursing","B.S Nursing",[{label:"Year 1",amount:"1,80,000"},{label:"Year 2",amount:"1,80,000"},{label:"Year 3",amount:"1,60,000"},{label:"Year 4",amount:"1,60,000"}]),
      mkFee("bba","BBA",[{label:"Year 1",amount:"1,50,000"},{label:"Year 2",amount:"1,50,000"},{label:"Year 3",amount:"1,50,000"}]),
    ]
  },
  { id:"a4", type:"abroad", name:"SEU University", location:"Tbilisi, Georgia", country:"Georgia", courses:["mbbs","nursing"], courseLabel:"MBBS · B.S Nursing", seats:"—", hidden:false, images:["https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80","https://images.unsplash.com/photo-1592280771190-3e2e4d571952?auto=format&fit=crop&w=800&q=80"], videos:[], brochure:"#", contact: { ...BLANK_CONTACT },
    feeStructures:[
      mkFee("mbbs","MBBS",[{label:"Year 1",amount:"4,20,000"},{label:"Year 2",amount:"4,00,000"},{label:"Year 3",amount:"4,00,000"},{label:"Year 4",amount:"3,80,000"},{label:"Year 5",amount:"3,80,000"},{label:"Year 6",amount:"3,50,000"}]),
      mkFee("nursing","B.S Nursing",[{label:"Year 1",amount:"2,00,000"},{label:"Year 2",amount:"2,00,000"},{label:"Year 3",amount:"1,80,000"},{label:"Year 4",amount:"1,80,000"}]),
    ]
  },
];

const IMG = [
  "https://images.unsplash.com/photo-1523050853051-f050540c4419?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1519452635265-7b1fbfd1e4e0?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1568667256549-094345857637?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1592280771190-3e2e4d571952?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1541339907198-e08756ebafe3?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1551190822-a9333d879b1f?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1547448415-e9f5b28e570d?auto=format&fit=crop&w=800&q=80",
];

const mk = (id,name,loc,cat,courses,img,extra={}) => ({
  id, type:"domestic", name, location:loc, state:"tamilnadu", city:"coimbatore",
  category:cat, courses, courseLabel:courses.map(c=>CLBL[c]).join(" · "),
  seats:"—", images:[IMG[img%IMG.length]], videos:[], brochure:"#",
  hidden: false,
  contact: { ...BLANK_CONTACT },
  feeStructures: courses.filter(c=>c!=="multi").map(c=>mkFee(c, CLBL[c]||c, [{label:"Year 1",amount:"Contact"},{label:"Year 2",amount:"Contact"}])),
  ...extra
});

const DEFAULT_DOMESTIC = [
  mk("d1","AMC (Karumathampatti)","Karumathampatti, Coimbatore","primary",["nursing","pharmacy","allied"],0),
  mk("d2","United College","Periyanaickenpalayam, Coimbatore","primary",["nursing","pharmacy","engineering","allied","arts"],1),
  mk("d3","Revathi College","Avinashi, Tamil Nadu","primary",["nursing","bpt","allied"],2),
  mk("d4","AVB College","Coimbatore, Tamil Nadu","primary",["pharmacy"],3),
  mk("d5","Sapthagiri NPS University","Bangalore, Karnataka","primary",["multi"],4,{state:"karnataka",city:"bangalore"}),
  mk("d6","Bhavani Nursing College","Tamil Nadu","primary",["nursing"],5),
  mk("d7","KVG Medical College","Mangalore, Karnataka","primary",["nursing"],6,{state:"karnataka",city:"mangalore"}),
  mk("d8","STG College","Mangalore, Karnataka","primary",["nursing"],7,{state:"karnataka",city:"mangalore"}),
  mk("d9","Karpagam Academy","Coimbatore, Tamil Nadu","secondary",["multi"],0),
  mk("d10","RVS College","Coimbatore, Tamil Nadu","secondary",["multi"],1),
  mk("d11","Hindusthan College","Coimbatore, Tamil Nadu","secondary",["multi"],2),
  mk("d12","SNS College","Coimbatore, Tamil Nadu","secondary",["multi"],3),
  mk("d13","INFO Institute","Coimbatore, Tamil Nadu","secondary",["multi"],4),
  mk("d14","PPG College","Coimbatore, Tamil Nadu","secondary",["multi"],5),
  mk("d15","KGISC College","Coimbatore, Tamil Nadu","secondary",["multi"],6),
  mk("d16","Dhanalakshmi Srinivasan College","Tamil Nadu","secondary",["multi"],7),
  mk("d17","JCT College","Coimbatore, Tamil Nadu","secondary",["multi"],8),
  mk("d18","Study World","Coimbatore, Tamil Nadu","secondary",["multi"],9),
  mk("d19","EASA College","Tamil Nadu","secondary",["multi"],0),
  mk("d20","Nehru College","Coimbatore, Tamil Nadu","secondary",["multi"],1),
  mk("d21","CMS College","Coimbatore, Tamil Nadu","secondary",["multi"],2),
  mk("d22","Rathinam College","Coimbatore, Tamil Nadu","secondary",["multi"],3),
  mk("d23","Sree Abirami College","Coimbatore, Tamil Nadu","secondary",["multi"],4),
  mk("d24","Kumaran College of Nursing","Coimbatore, Tamil Nadu","secondary",["nursing"],5),
  mk("d25","Annai Meenakshi College of Nursing","Tamil Nadu","secondary",["nursing"],6),
  mk("d26","Nightingale Nursing College","Tamil Nadu","secondary",["nursing"],7),
  mk("d27","Ennam Nursing College","Tamil Nadu","secondary",["nursing"],8),
  mk("d28","Sri Sai College of Nursing","Tamil Nadu","secondary",["nursing"],9),
  mk("d29","Gem Hospital","Coimbatore, Tamil Nadu","secondary",["multi"],0),
  mk("d30","Ganga Hospital","Coimbatore, Tamil Nadu","secondary",["multi"],1),
  mk("d31","Royalcare Hospital","Coimbatore, Tamil Nadu","secondary",["multi"],2),
  mk("d32","Sree Sakthi College","Coimbatore, Tamil Nadu","secondary",["multi"],3),
  mk("d33","Kalaignar Karunanidhi Institute","Tamil Nadu","secondary",["multi"],4),
  mk("d34","Dr. NGP College","Coimbatore, Tamil Nadu","secondary",["arts","engineering"],5),
  mk("d35","PSG College of Technology","Coimbatore, Tamil Nadu","premium",["multi"],6),
  mk("d36","KMCH College","Coimbatore, Tamil Nadu","premium",["multi"],7),
  mk("d37","Sri Krishna College of Technology","Coimbatore, Tamil Nadu","premium",["engineering","multi"],8),
  mk("d38","Kumaraguru College of Technology","Coimbatore, Tamil Nadu","premium",["engineering","multi"],9),
  mk("d39","Bannari Amman Institute of Technology","Sathyamangalam, Tamil Nadu","premium",["engineering","multi"],0),
  mk("d40","CIT - Coimbatore Institute of Technology","Coimbatore, Tamil Nadu","premium",["engineering","multi"],1),
  mk("d41","Sri Ramakrishna Engineering College","Coimbatore, Tamil Nadu","premium",["engineering","multi"],2),
  mk("d42","Eswar College of Engineering","Coimbatore, Tamil Nadu","premium",["engineering","multi"],3),
  mk("d43","KPR Institute of Engineering","Coimbatore, Tamil Nadu","premium",["engineering","multi"],4),
];

// ─── STYLES ──────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  :root {
    --ink:#0a0a0f; --ink2:#3d3d4a; --ink3:#8b8b9e;
    --paper:#f5f4f0; --paper2:#eeece8; --paper3:#e4e2dc;
    --white:#ffffff;
    --accent:#e84c1e; --green:#16a34a;
    --r-card:20px; --r-modal:28px;
    --ease:cubic-bezier(0.4,0,0.2,1);
    --header-h:60px;
    --bottom-h:64px;
  }
  html { scroll-behavior:smooth; -webkit-text-size-adjust:100%; }
  body { font-family:'DM Sans',sans-serif; background:var(--paper); color:var(--ink);
         min-height:100vh; overflow-x:hidden; -webkit-font-smoothing:antialiased; }

  .header {
    position:sticky; top:0; z-index:200;
    background:rgba(245,244,240,0.92); backdrop-filter:blur(20px);
    border-bottom:1px solid var(--paper3);
    height:var(--header-h); padding:0 16px;
    display:flex; align-items:center; justify-content:space-between; gap:12px;
  }
  .logo { display:flex; align-items:center; gap:8px; cursor:pointer; flex-shrink:0; }
  .logo-mark { width:32px; height:32px; background:var(--ink); border-radius:8px;
               display:flex; align-items:center; justify-content:center;
               color:var(--paper); font-family:'Syne',sans-serif; font-size:14px; font-weight:800; }
  .logo-text { font-family:'Syne',sans-serif; font-weight:800; font-size:16px; letter-spacing:-0.3px; }
  .logo-accent { color:var(--accent); }
  .header-right { display:flex; align-items:center; gap:8px; }
  .admin-btn {
    padding:7px 13px; background:transparent; border:1.5px solid var(--paper3);
    border-radius:10px; font-family:'Syne',sans-serif; font-size:12px; font-weight:700;
    color:var(--ink3); cursor:pointer; transition:all .2s; white-space:nowrap;
  }
  .admin-btn:hover, .admin-btn.active-admin { background:var(--ink); color:white; border-color:var(--ink); }
  .filter-toggle-btn {
    display:none; align-items:center; gap:6px;
    padding:7px 13px; background:var(--white); border:1.5px solid var(--paper3);
    border-radius:10px; font-family:'Syne',sans-serif; font-size:12px; font-weight:700;
    color:var(--ink2); cursor:pointer; transition:all .2s; position:relative;
  }
  .filter-toggle-btn.has-filters { border-color:var(--ink); color:var(--ink); }
  .filter-badge {
    position:absolute; top:-6px; right:-6px; width:18px; height:18px;
    background:var(--accent); color:white; border-radius:50%;
    font-size:10px; font-weight:700; display:flex; align-items:center; justify-content:center;
    border:2px solid var(--paper);
  }
  .tab-group {
    display:flex; background:var(--paper2); border:1px solid var(--paper3);
    border-radius:12px; padding:3px; gap:2px;
  }
  .tab-btn {
    padding:7px 18px; border-radius:9px; font-family:'Syne',sans-serif;
    font-size:13px; font-weight:700; border:none; cursor:pointer;
    transition:all .2s var(--ease); color:var(--ink3); background:transparent;
    white-space:nowrap;
  }
  .tab-btn.active { background:var(--ink); color:var(--paper); box-shadow:0 2px 6px rgba(0,0,0,.18); }
  .bottom-tabs {
    display:none; position:fixed; bottom:0; left:0; right:0; z-index:200;
    background:rgba(245,244,240,0.96); backdrop-filter:blur(20px);
    border-top:1px solid var(--paper3); height:var(--bottom-h);
    align-items:stretch;
  }
  .bottom-tab {
    flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center;
    gap:3px; border:none; background:transparent; cursor:pointer;
    font-family:'Syne',sans-serif; font-size:10px; font-weight:700;
    color:var(--ink3); transition:color .2s; padding-bottom:env(safe-area-inset-bottom,0);
  }
  .bottom-tab.active { color:var(--ink); }
  .bottom-tab-icon { font-size:20px; line-height:1; }
  .bottom-tab-dot { width:4px; height:4px; border-radius:50%; background:var(--accent); margin-top:1px; opacity:0; transition:opacity .2s; }
  .bottom-tab.active .bottom-tab-dot { opacity:1; }

  .layout { display:flex; gap:20px; padding:20px 20px 24px; max-width:1440px; margin:0 auto; align-items:flex-start; }
  .sidebar-wrap { width:260px; flex-shrink:0; position:sticky; top:calc(var(--header-h) + 12px); max-height:calc(100vh - var(--header-h) - 24px); overflow-y:auto; scrollbar-width:thin; scrollbar-color:var(--paper3) transparent; }
  .sidebar-wrap::-webkit-scrollbar { width:3px; }
  .sidebar-wrap::-webkit-scrollbar-thumb { background:var(--paper3); border-radius:99px; }
  .sidebar-inner { background:var(--white); border:1px solid var(--paper3); border-radius:var(--r-card); padding:20px; display:flex; flex-direction:column; gap:20px; }
  .filter-section { display:flex; flex-direction:column; gap:8px; }
  .filter-label { font-family:'Syne',sans-serif; font-size:9px; font-weight:800; letter-spacing:1.4px; text-transform:uppercase; color:var(--ink3); display:flex; align-items:center; justify-content:space-between; }
  .counter-pill { background:var(--ink); color:var(--paper); font-size:9px; font-weight:700; padding:2px 7px; border-radius:99px; font-family:'DM Sans',sans-serif; }
  .search-wrap { position:relative; }
  .search-wrap svg { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:var(--ink3); pointer-events:none; }
  .search-wrap input { width:100%; padding:10px 10px 10px 36px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:12px; font-family:'DM Sans',sans-serif; font-size:14px; color:var(--ink); outline:none; transition:border-color .2s, box-shadow .2s; }
  .search-wrap input:focus { border-color:var(--ink); box-shadow:0 0 0 3px rgba(10,10,15,.06); }
  .search-wrap input::placeholder { color:var(--ink3); }
  .check-list { display:flex; flex-direction:column; gap:2px; }
  .check-label { display:flex; align-items:center; gap:10px; cursor:pointer; padding:7px 9px; border-radius:9px; transition:background .15s; }
  .check-label:hover { background:var(--paper); }
  .check-label input[type="checkbox"] { appearance:none; width:17px; height:17px; flex-shrink:0; border:2px solid var(--paper3); border-radius:5px; cursor:pointer; position:relative; transition:all .15s; }
  .check-label input[type="checkbox"]:checked { background:var(--ink); border-color:var(--ink); }
  .check-label input[type="checkbox"]:checked::after { content:''; position:absolute; left:3px; top:1px; width:6px; height:9px; border:2px solid white; border-top:none; border-left:none; transform:rotate(45deg); }
  .check-label span { font-size:13px; font-weight:500; color:var(--ink2); }
  .sidebar-select { width:100%; padding:9px 34px 9px 11px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:11px; font-family:'DM Sans',sans-serif; font-size:13px; color:var(--ink); outline:none; cursor:pointer; -webkit-appearance:none; appearance:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' fill='%238b8b9e' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 11px center; transition:border-color .2s; }
  .sidebar-select:focus { border-color:var(--ink); }
  .s-divider { height:1px; background:var(--paper2); }
  .reset-btn { width:100%; padding:11px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:11px; font-family:'Syne',sans-serif; font-size:12px; font-weight:700; color:var(--ink2); cursor:pointer; transition:all .2s; }
  .reset-btn:hover { background:var(--ink); color:white; border-color:var(--ink); }
  .cat-chips { display:flex; gap:6px; flex-wrap:wrap; }
  .cat-chip { padding:5px 12px; border-radius:99px; font-family:'Syne',sans-serif; font-size:11px; font-weight:700; border:1.5px solid var(--paper3); background:var(--paper); color:var(--ink3); cursor:pointer; transition:all .2s; }
  .cat-chip.active-primary   { background:#dcfce7; color:#15803d; border-color:#86efac; }
  .cat-chip.active-secondary { background:#e0e7ff; color:#4338ca; border-color:#a5b4fc; }
  .cat-chip.active-premium   { background:#fef9c3; color:#a16207; border-color:#fde047; }

  main { flex:1; min-width:0; }
  .stats-bar { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; gap:10px; }
  .stats-bar h2 { font-family:'Syne',sans-serif; font-size:20px; font-weight:700; letter-spacing:-.4px; }
  .stats-count { color:var(--ink3); font-weight:400; font-size:15px; }
  .add-btn-top { display:flex; align-items:center; gap:6px; padding:8px 16px; background:var(--ink); color:white; border:none; border-radius:10px; font-family:'Syne',sans-serif; font-size:12px; font-weight:700; cursor:pointer; transition:all .2s; white-space:nowrap; flex-shrink:0; }
  .add-btn-top:hover { background:var(--accent); }

  .card-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:14px; }
  .card { background:var(--white); border-radius:var(--r-card); overflow:hidden; border:1.5px solid var(--paper2); transition:transform .3s var(--ease), box-shadow .3s var(--ease), border-color .3s var(--ease); cursor:pointer; animation:cardIn .4s var(--ease) both; }
  .card:hover { transform:translateY(-4px); box-shadow:0 20px 40px rgba(0,0,0,.09); border-color:var(--ink); }
  @keyframes cardIn { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  .card-img { position:relative; height:180px; overflow:hidden; }
  .card-img img { width:100%; height:100%; object-fit:cover; transition:transform .5s var(--ease); }
  .card:hover .card-img img { transform:scale(1.05); }
  .card-badge { position:absolute; top:10px; left:10px; background:rgba(10,10,15,.72); backdrop-filter:blur(8px); color:white; font-size:9px; font-weight:700; letter-spacing:1px; text-transform:uppercase; padding:4px 10px; border-radius:99px; }
  .seats-badge { position:absolute; top:10px; right:10px; background:rgba(255,255,255,.92); backdrop-filter:blur(8px); color:var(--ink); font-size:9px; font-weight:700; padding:4px 9px; border-radius:99px; display:flex; align-items:center; gap:4px; }
  .seats-dot { width:5px; height:5px; background:var(--green); border-radius:50%; animation:blink 2s infinite; }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:.4} }
  .card-body { padding:14px 16px 16px; }
  .card-tags { display:flex; flex-wrap:wrap; gap:4px; margin-bottom:8px; }
  .tag { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.6px; padding:3px 8px; border-radius:5px; }
  .tag-mbbs{background:#dbeafe;color:#1d4ed8} .tag-mba{background:#fef3c7;color:#b45309}
  .tag-nursing{background:#fce7f3;color:#be185d} .tag-bba{background:#f3e8ff;color:#7c3aed}
  .tag-pharmacy{background:#ecfdf5;color:#065f46} .tag-allied{background:#fff7ed;color:#c2410c}
  .tag-engineering{background:#f0fdf4;color:#166534} .tag-arts{background:#fdf4ff;color:#86198f}
  .tag-bpt{background:#ffe4e6;color:#9f1239} .tag-multi{background:#f1f5f9;color:#475569}
  .cat-primary{background:#dcfce7;color:#15803d} .cat-secondary{background:#e0e7ff;color:#4338ca}
  .cat-premium{background:#fef9c3;color:#a16207}
  .card-name { font-family:'Syne',sans-serif; font-size:15px; font-weight:700; letter-spacing:-.2px; line-height:1.3; color:var(--ink); margin-bottom:4px; min-height:40px; }
  .card-loc { font-size:11px; color:var(--ink3); display:flex; align-items:center; gap:3px; margin-bottom:12px; }
  .card-actions { display:flex; gap:7px; }
  .btn-details { flex:1; padding:10px; background:var(--ink); color:white; border:none; border-radius:10px; font-family:'Syne',sans-serif; font-size:12px; font-weight:700; cursor:pointer; transition:background .2s; }
  .btn-details:hover { background:var(--accent); }
  .btn-icon { padding:10px 12px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:10px; cursor:pointer; font-size:13px; transition:all .2s; line-height:1; }
  .btn-icon:hover { background:#fef3c7; border-color:#b45309; }
  .btn-icon.del:hover { background:#fee2e2; border-color:#dc2626; }

  .no-results { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 20px; text-align:center; grid-column:1/-1; }
  .no-results-icon { width:64px; height:64px; background:var(--paper2); border-radius:18px; display:flex; align-items:center; justify-content:center; font-size:28px; margin-bottom:14px; }
  .no-results h3 { font-family:'Syne',sans-serif; font-size:18px; font-weight:700; margin-bottom:6px; }
  .no-results p  { color:var(--ink3); font-size:13px; }

  .modal-backdrop { position:fixed; inset:0; z-index:300; display:flex; align-items:center; justify-content:center; padding:16px; }
  .modal-overlay { position:absolute; inset:0; background:rgba(10,10,15,.65); backdrop-filter:blur(10px); animation:fadeIn .25s var(--ease); }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  .modal-box { position:relative; width:100%; max-width:960px; max-height:92vh; background:var(--white); border-radius:var(--r-modal); overflow:hidden; display:flex; animation:slideUp .3s var(--ease); box-shadow:0 40px 80px rgba(0,0,0,.28); }
  @keyframes slideUp { from{opacity:0;transform:translateY(28px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
  .modal-media { width:40%; flex-shrink:0; background:var(--ink); display:flex; flex-direction:column; overflow:hidden; }
  .carousel { position:relative; flex:1; min-height:240px; overflow:hidden; }
  .carousel img { width:100%; height:100%; object-fit:cover; display:block; }
  .car-btn { position:absolute; top:50%; transform:translateY(-50%); width:34px; height:34px; background:rgba(255,255,255,.9); border:none; border-radius:50%; cursor:pointer; font-size:17px; display:flex; align-items:center; justify-content:center; transition:all .2s; color:var(--ink); z-index:2; }
  .car-btn:hover { background:white; transform:translateY(-50%) scale(1.08); }
  .car-btn.prev { left:10px; }
  .car-btn.next { right:10px; }
  .car-dots { position:absolute; bottom:10px; left:50%; transform:translateX(-50%); display:flex; gap:5px; }
  .cdot { width:5px; height:5px; border-radius:3px; background:rgba(255,255,255,.4); cursor:pointer; transition:all .2s; border:none; }
  .cdot.active { background:white; width:16px; }
  .modal-videos { background:#0d0d0d; }
  .modal-video-tabs { display:flex; gap:2px; padding:8px 8px 0; overflow-x:auto; scrollbar-width:none; }
  .modal-video-tabs::-webkit-scrollbar { display:none; }
  .modal-video-tab { padding:5px 12px; border-radius:6px 6px 0 0; font-family:'Syne',sans-serif; font-size:10px; font-weight:700; border:none; cursor:pointer; background:rgba(255,255,255,.1); color:rgba(255,255,255,.5); transition:all .2s; white-space:nowrap; flex-shrink:0; }
  .modal-video-tab.active { background:rgba(255,255,255,.2); color:white; }
  .modal-video-player { padding:0 8px 8px; }
  .modal-video-player iframe { width:100%; aspect-ratio:16/9; border-radius:8px; display:block; border:none; }
  .modal-video-player video { width:100%; border-radius:8px; display:block; max-height:200px; }
  .modal-info { flex:1; overflow-y:auto; padding:24px 28px; display:flex; flex-direction:column; gap:0; }
  .modal-info::-webkit-scrollbar { width:3px; }
  .modal-info::-webkit-scrollbar-thumb { background:var(--paper3); border-radius:99px; }
  .modal-tags { display:flex; flex-wrap:wrap; gap:5px; margin-bottom:10px; }
  .modal-title { font-family:'Syne',sans-serif; font-size:20px; font-weight:800; letter-spacing:-.5px; line-height:1.2; margin-bottom:16px; }
  .modal-close { position:absolute; top:14px; right:14px; width:32px; height:32px; background:var(--ink); color:white; border:none; border-radius:50%; cursor:pointer; font-size:13px; display:flex; align-items:center; justify-content:center; transition:all .2s; z-index:10; }
  .modal-close:hover { background:var(--accent); transform:rotate(90deg); }
  .info-rows { display:flex; flex-direction:column; margin-bottom:18px; }
  .info-row { display:flex; align-items:flex-start; gap:12px; padding:10px 0; border-bottom:1px solid var(--paper2); }
  .info-row:last-child { border-bottom:none; }
  .info-icon { width:34px; height:34px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
  .info-icon.loc{background:#dbeafe} .info-icon.course{background:#fef3c7} .info-icon.seats{background:#dcfce7} .info-icon.contact{background:#f3e8ff}
  .info-lbl { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:var(--ink3); margin-bottom:2px; }
  .info-val { font-size:13px; font-weight:500; color:var(--ink); }
  .seats-val { color:var(--green); font-weight:700; }
  .contact-section { margin-bottom:18px; }
  .contact-section-title { font-family:'Syne',sans-serif; font-size:10px; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; color:var(--ink3); margin-bottom:10px; padding-bottom:8px; border-bottom:2px solid var(--paper2); display:flex; align-items:center; gap:6px; }
  .contact-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .contact-item { background:var(--paper); border:1.5px solid var(--paper3); border-radius:11px; padding:10px 13px; display:flex; align-items:center; gap:10px; text-decoration:none; transition:border-color .2s, background .2s; }
  .contact-item:hover { border-color:var(--ink); background:var(--white); }
  .contact-item-icon { width:30px; height:30px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
  .contact-item-icon.ph { background:#dcfce7; }
  .contact-item-icon.em { background:#dbeafe; }
  .contact-item-icon.wa { background:#d1fae5; }
  .contact-item-icon.nm { background:#f3e8ff; }
  .contact-item-body { min-width:0; }
  .contact-item-lbl { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.8px; color:var(--ink3); margin-bottom:1px; }
  .contact-item-val { font-size:12px; font-weight:600; color:var(--ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .contact-na { grid-column:1/-1; text-align:center; padding:14px; color:var(--ink3); font-size:12px; background:var(--paper); border-radius:10px; border:1.5px dashed var(--paper3); }
  .fee-section { margin-bottom:18px; }
  .fee-section-title { font-family:'Syne',sans-serif; font-size:10px; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; color:var(--ink3); margin-bottom:10px; padding-bottom:8px; border-bottom:2px solid var(--paper2); display:flex; align-items:center; gap:6px; }
  .fee-tabs { display:flex; gap:5px; flex-wrap:wrap; margin-bottom:12px; }
  .fee-tab { padding:5px 12px; border-radius:8px; font-family:'Syne',sans-serif; font-size:10px; font-weight:700; border:1.5px solid var(--paper3); background:var(--paper); color:var(--ink3); cursor:pointer; transition:all .2s; }
  .fee-tab.active { background:var(--ink); color:white; border-color:var(--ink); }
  .fee-table-wrap { background:var(--paper); border-radius:12px; overflow:hidden; border:1px solid var(--paper3); }
  .fee-table { width:100%; border-collapse:collapse; }
  .fee-table th { background:var(--ink); color:white; padding:8px 14px; text-align:left; font-family:'Syne',sans-serif; font-size:9px; font-weight:700; letter-spacing:1px; text-transform:uppercase; }
  .fee-table td { padding:9px 14px; font-size:12px; border-bottom:1px solid var(--paper3); }
  .fee-table tr:last-child td { border-bottom:none; }
  .fee-table tr:nth-child(even) td { background:rgba(0,0,0,.02); }
  .fee-year { font-weight:600; color:var(--ink2); }
  .fee-amount { font-family:'Syne',sans-serif; font-weight:700; color:var(--ink); font-size:13px; }
  .fee-total-row td { background:var(--ink) !important; color:white; font-family:'Syne',sans-serif; font-weight:700; font-size:12px; border:none; }
  .fee-total-amt { font-size:14px; }
  .fee-note { font-size:10px; color:var(--ink3); margin-top:6px; padding:0 4px; }
  .fee-brochure-link { display:inline-flex; align-items:center; gap:7px; margin-top:10px; margin-right:8px; font-family:'Syne',sans-serif; font-size:11px; font-weight:700; text-decoration:none; padding:9px 16px; border-radius:10px; border:2px solid transparent; transition:all .2s var(--ease); box-shadow:0 2px 8px rgba(0,0,0,.08); }
  .fee-brochure-link.fee-sheet-btn { background:#fff7ed; color:#c2410c; border-color:#fed7aa; }
  .fee-brochure-link.fee-sheet-btn:hover { background:#c2410c; color:white; border-color:#c2410c; box-shadow:0 6px 18px rgba(194,65,12,.28); transform:translateY(-1px); }
  .fee-brochure-link.brochure-btn { background:#eff6ff; color:#1d4ed8; border-color:#bfdbfe; }
  .fee-brochure-link.brochure-btn:hover { background:#1d4ed8; color:white; border-color:#1d4ed8; box-shadow:0 6px 18px rgba(29,78,216,.28); transform:translateY(-1px); }
  .fee-download-row { display:flex; flex-wrap:wrap; gap:4px; margin-top:4px; }
  .fee-seats-badge { display:inline-flex; align-items:center; gap:5px; background:#dcfce7; color:#15803d; border:1.5px solid #86efac; padding:4px 11px; border-radius:99px; font-family:'Syne',sans-serif; font-size:10px; font-weight:700; margin-bottom:10px; }
  .fee-seats-dot { width:5px; height:5px; border-radius:50%; background:#16a34a; animation:blink 2s infinite; }
  .hostel-section { margin-top:14px; padding-top:12px; border-top:1.5px dashed var(--paper3); }
  .hostel-section-title { font-family:'Syne',sans-serif; font-size:9px; font-weight:800; letter-spacing:1.4px; text-transform:uppercase; color:var(--ink3); margin-bottom:8px; display:flex; align-items:center; gap:5px; }
  .hostel-table { width:100%; border-collapse:collapse; margin-top:4px; }
  .hostel-table th { background:#1e3a5f; color:white; padding:7px 12px; text-align:left; font-family:'Syne',sans-serif; font-size:9px; font-weight:700; letter-spacing:1px; text-transform:uppercase; }
  .hostel-table td { padding:8px 12px; font-size:12px; border-bottom:1px solid var(--paper3); }
  .hostel-table tr:last-child td { border-bottom:none; }
  .hostel-table tr:nth-child(even) td { background:rgba(0,0,0,.02); }
  .hostel-mul-chip { display:inline-flex; align-items:center; gap:3px; background:#e0f2fe; color:#0369a1; border:1px solid #bae6fd; padding:2px 7px; border-radius:5px; font-family:'Syne',sans-serif; font-size:9px; font-weight:700; }
  .hostel-total-row td { background:#1e3a5f !important; color:white; font-family:'Syne',sans-serif; font-weight:700; font-size:12px; border:none; }
  .hostel-editor-wrap { margin-top:10px; padding-top:10px; border-top:1.5px dashed var(--paper3); }
  .hostel-row-grid { display:grid; grid-template-columns:1fr 1fr auto auto; gap:7px; align-items:center; }
  .hostel-mul-select { padding:7px 8px; background:var(--white); border:1.5px solid var(--paper3); border-radius:8px; font-family:'Syne',sans-serif; font-size:11px; font-weight:700; color:var(--ink2); outline:none; cursor:pointer; -webkit-appearance:none; appearance:none; transition:border-color .2s; }
  .hostel-mul-select:focus { border-color:var(--ink); }
  .modal-ctas { display:flex; flex-direction:column; gap:8px; margin-top:auto; padding-top:4px; }
  .cta { display:flex; align-items:center; justify-content:center; gap:7px; width:100%; padding:12px; border-radius:12px; font-family:'Syne',sans-serif; font-size:13px; font-weight:700; text-decoration:none; transition:all .2s; border:2px solid transparent; }
  .cta-p { background:var(--ink); color:white; border-color:var(--ink); }
  .cta-p:hover { background:var(--accent); border-color:var(--accent); }
  .cta-s { background:transparent; color:var(--ink); border-color:var(--paper3); }
  .cta-s:hover { border-color:var(--ink); background:var(--paper); }
  .cta-wa { background:#16a34a; color:white; border-color:#16a34a; }
  .cta-wa:hover { background:#15803d; border-color:#15803d; }
  .drawer-backdrop { display:none; position:fixed; inset:0; z-index:250; background:rgba(10,10,15,.5); backdrop-filter:blur(4px); animation:fadeIn .2s var(--ease); }
  .drawer-backdrop.open { display:block; }
  .drawer { position:fixed; bottom:0; left:0; right:0; z-index:260; background:var(--white); border-radius:20px 20px 0 0; padding:0 0 env(safe-area-inset-bottom,16px); max-height:88vh; overflow-y:auto; transform:translateY(100%); transition:transform .35s var(--ease); }
  .drawer.open { transform:translateY(0); }
  .drawer-handle { width:36px; height:4px; background:var(--paper3); border-radius:2px; margin:12px auto 0; }
  .drawer-head { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid var(--paper2); }
  .drawer-head h3 { font-family:'Syne',sans-serif; font-size:16px; font-weight:800; }
  .drawer-close { width:30px; height:30px; background:var(--paper); border:none; border-radius:50%; cursor:pointer; font-size:13px; display:flex; align-items:center; justify-content:center; }
  .drawer-body { padding:20px; display:flex; flex-direction:column; gap:20px; }
  .drawer-apply { width:100%; padding:14px; background:var(--ink); color:white; border:none; border-radius:14px; font-family:'Syne',sans-serif; font-size:14px; font-weight:700; cursor:pointer; margin-top:4px; }
  .admin-panel { background:var(--white); border:1.5px solid var(--paper3); border-radius:var(--r-card); overflow:hidden; margin:20px; animation:cardIn .3s var(--ease) both; }
  .admin-head { background:var(--ink); color:white; padding:20px 24px; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; }
  .admin-head h2 { font-family:'Syne',sans-serif; font-size:18px; font-weight:800; }
  .admin-head p  { font-size:12px; opacity:.6; margin-top:2px; }
  .admin-add { display:flex; align-items:center; gap:6px; padding:9px 18px; background:var(--accent); color:white; border:none; border-radius:10px; font-family:'Syne',sans-serif; font-size:12px; font-weight:700; cursor:pointer; transition:all .2s; }
  .admin-add:hover { background:#c93d12; }
  .table-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
  table { width:100%; border-collapse:collapse; min-width:640px; }
  thead { background:var(--paper); }
  th { padding:10px 14px; text-align:left; font-family:'Syne',sans-serif; font-size:9px; font-weight:700; letter-spacing:1.2px; text-transform:uppercase; color:var(--ink3); border-bottom:1px solid var(--paper3); white-space:nowrap; }
  td { padding:12px 14px; border-bottom:1px solid var(--paper2); font-size:12px; vertical-align:middle; }
  tr:last-child td { border-bottom:none; }
  tr:hover td { background:var(--paper); }
  .td-name { font-family:'Syne',sans-serif; font-weight:700; font-size:12px; max-width:180px; }
  .td-loc  { color:var(--ink3); max-width:140px; font-size:11px; }
  .td-tags { display:flex; flex-wrap:wrap; gap:3px; max-width:180px; }
  .td-img  { width:44px; height:32px; border-radius:6px; object-fit:cover; }
  .td-acts { display:flex; gap:6px; }
  .btn-sm { padding:5px 12px; border-radius:7px; font-family:'Syne',sans-serif; font-size:10px; font-weight:700; cursor:pointer; border:1.5px solid transparent; transition:all .2s; }
  .btn-edit { background:#fef3c7; color:#b45309; border-color:#fde68a; }
  .btn-edit:hover { background:#fde68a; }
  .btn-del  { background:#fee2e2; color:#dc2626; border-color:#fecaca; }
  .btn-del:hover  { background:#fecaca; }
  .type-badge { display:inline-flex; align-items:center; gap:4px; padding:2px 9px; border-radius:99px; font-size:9px; font-weight:700; }
  .type-abroad   { background:#dbeafe; color:#1d4ed8; }
  .type-domestic { background:#dcfce7; color:#15803d; }
  .section-row td { background:var(--paper2); padding:8px 14px; font-family:'Syne',sans-serif; font-weight:800; font-size:9px; letter-spacing:1.5px; text-transform:uppercase; color:var(--ink3); }
  .form-backdrop { position:fixed; inset:0; z-index:350; display:flex; align-items:center; justify-content:center; padding:16px; }
  .form-overlay  { position:absolute; inset:0; background:rgba(10,10,15,.7); backdrop-filter:blur(8px); }
  .form-box { position:relative; width:100%; max-width:680px; max-height:94vh; background:var(--white); border-radius:24px; overflow-y:auto; animation:slideUp .28s var(--ease); box-shadow:0 28px 60px rgba(0,0,0,.22); }
  .form-box::-webkit-scrollbar { width:3px; }
  .form-box::-webkit-scrollbar-thumb { background:var(--paper3); border-radius:99px; }
  .form-head { padding:24px 24px 0; display:flex; align-items:flex-start; justify-content:space-between; }
  .form-head h3 { font-family:'Syne',sans-serif; font-size:20px; font-weight:800; letter-spacing:-.4px; }
  .form-head p  { font-size:12px; color:var(--ink3); margin-top:3px; }
  .form-close { width:30px; height:30px; background:var(--paper); border:none; border-radius:50%; cursor:pointer; font-size:13px; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:all .2s; }
  .form-close:hover { background:var(--ink); color:white; }
  .form-body { padding:20px 24px 28px; display:flex; flex-direction:column; gap:16px; }
  .form-row  { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .f-group   { display:flex; flex-direction:column; gap:5px; }
  .f-group.full { grid-column:1/-1; }
  .f-label   { font-family:'Syne',sans-serif; font-size:9px; font-weight:800; letter-spacing:1.2px; text-transform:uppercase; color:var(--ink3); }
  .f-input   { padding:10px 12px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:11px; font-family:'DM Sans',sans-serif; font-size:14px; color:var(--ink); outline:none; transition:all .2s; width:100%; }
  .f-input:focus { border-color:var(--ink); box-shadow:0 0 0 3px rgba(10,10,15,.05); }
  .f-select  { padding:9px 32px 9px 12px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:11px; font-family:'DM Sans',sans-serif; font-size:13px; color:var(--ink); outline:none; cursor:pointer; -webkit-appearance:none; appearance:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' fill='%238b8b9e' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 10px center; transition:all .2s; width:100%; }
  .f-select:focus { border-color:var(--ink); }
  .courses-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:5px; }
  .course-check { display:flex; align-items:center; gap:7px; padding:7px 10px; border-radius:9px; cursor:pointer; background:var(--paper); border:1.5px solid var(--paper3); transition:border-color .15s; }
  .course-check:hover { border-color:var(--ink3); }
  .course-check input { appearance:none; width:15px; height:15px; border:2px solid var(--paper3); border-radius:4px; cursor:pointer; position:relative; transition:all .15s; flex-shrink:0; }
  .course-check input:checked { background:var(--ink); border-color:var(--ink); }
  .course-check input:checked::after { content:''; position:absolute; left:3px; top:1px; width:5px; height:8px; border:2px solid white; border-top:none; border-left:none; transform:rotate(45deg); }
  .course-check span { font-size:12px; font-weight:500; color:var(--ink2); }
  .f-divider { height:1px; background:var(--paper2); }
  .f-section { font-family:'Syne',sans-serif; font-size:10px; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; color:var(--ink3); padding-bottom:4px; border-bottom:2px solid var(--paper2); }
  .form-actions { display:flex; gap:8px; padding-top:4px; }
  .btn-save   { flex:1; padding:13px; background:var(--ink); color:white; border:none; border-radius:12px; font-family:'Syne',sans-serif; font-size:13px; font-weight:700; cursor:pointer; transition:all .2s; }
  .btn-save:hover { background:var(--green); }
  .btn-cancel { padding:13px 20px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:12px; font-family:'Syne',sans-serif; font-size:13px; font-weight:700; color:var(--ink2); cursor:pointer; transition:all .2s; }
  .btn-cancel:hover { border-color:var(--ink); color:var(--ink); }
  .hidden-overlay { position:absolute; inset:0; background:rgba(10,10,15,.55); display:flex; align-items:center; justify-content:center; z-index:2; }
  .hidden-badge-card { background:rgba(10,10,15,.8); color:white; font-family:'Syne',sans-serif; font-size:10px; font-weight:800; letter-spacing:1px; padding:5px 12px; border-radius:99px; display:flex; align-items:center; gap:5px; }
  .btn-hide { padding:5px 10px; border-radius:7px; font-family:'Syne',sans-serif; font-size:10px; font-weight:700; cursor:pointer; border:1.5px solid; transition:all .2s; }
  .btn-hide-on  { background:#fef9c3; color:#a16207; border-color:#fde047; }
  .btn-hide-on:hover  { background:#fde047; }
  .btn-hide-off { background:#f3e8ff; color:#7c3aed; border-color:#d8b4fe; }
  .btn-hide-off:hover { background:#d8b4fe; }
  .card.is-hidden { opacity:.55; }
  .card.is-hidden:hover { opacity:.75; }
  .video-preview { position:relative; background:#000; border-radius:10px; overflow:hidden; margin-top:8px; }
  .video-preview video { width:100%; display:block; max-height:180px; border-radius:10px; }
  .video-preview-rm { position:absolute; top:6px; right:6px; width:24px; height:24px; background:rgba(220,38,38,.9); border:none; border-radius:50%; cursor:pointer; color:white; font-size:10px; display:flex; align-items:center; justify-content:center; }
  .video-list { display:flex; flex-direction:column; gap:10px; }
  .video-item-card { background:var(--paper); border:1.5px solid var(--paper3); border-radius:12px; overflow:hidden; }
  .video-item-head { display:flex; align-items:center; justify-content:space-between; padding:8px 12px; background:var(--paper2); }
  .video-item-head span { font-family:'Syne',sans-serif; font-size:11px; font-weight:800; color:var(--ink2); }
  .video-item-body { padding:10px 12px; }
  .btn-add-video { width:100%; padding:10px; background:var(--white); border:1.5px dashed var(--paper3); border-radius:10px; font-family:'Syne',sans-serif; font-size:11px; font-weight:700; color:var(--ink3); cursor:pointer; transition:all .2s; margin-top:4px; }
  .btn-add-video:hover { border-color:var(--ink); color:var(--ink); }
  .fee-editor { display:flex; flex-direction:column; gap:10px; }
  .fee-editor-card { background:var(--paper); border:1.5px solid var(--paper3); border-radius:12px; overflow:hidden; }
  .fee-editor-head { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; background:var(--paper2); }
  .fee-editor-head span { font-family:'Syne',sans-serif; font-size:11px; font-weight:800; color:var(--ink2); }
  .fee-editor-body { padding:10px 14px; display:flex; flex-direction:column; gap:6px; }
  .fee-year-row { display:grid; grid-template-columns:1fr 1fr auto; gap:7px; align-items:center; }
  .fee-year-row input { padding:7px 10px; background:var(--white); border:1.5px solid var(--paper3); border-radius:8px; font-family:'DM Sans',sans-serif; font-size:12px; color:var(--ink); outline:none; transition:border-color .2s; width:100%; }
  .fee-year-row input:focus { border-color:var(--ink); }
  .fee-year-row input:disabled { opacity:0.4; cursor:not-allowed; }
  .fee-amount-input { padding:7px 10px 7px 22px !important; width:100%; background:var(--white); border:1.5px solid var(--paper3); border-radius:8px; font-family:'DM Sans',sans-serif; font-size:12px; color:var(--ink); outline:none; transition:border-color .2s; }
  .fee-amount-input:focus { border-color:var(--ink); }
  .fee-amount-input:disabled { opacity:0.4; cursor:not-allowed; }
  .btn-rm-year { width:26px; height:26px; background:#fee2e2; border:none; border-radius:6px; cursor:pointer; font-size:10px; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
  .btn-add-year { padding:6px 12px; background:var(--white); border:1.5px dashed var(--paper3); border-radius:8px; font-family:'Syne',sans-serif; font-size:10px; font-weight:700; color:var(--ink3); cursor:pointer; width:100%; transition:all .2s; margin-top:2px; }
  .btn-add-year:hover { border-color:var(--ink); color:var(--ink); }
  .upload-tabs { display:flex; background:var(--paper2); border-radius:9px; padding:3px; gap:2px; margin-bottom:10px; }
  .upload-tab { flex:1; padding:6px 10px; border-radius:7px; font-family:'Syne',sans-serif; font-size:10px; font-weight:700; border:none; cursor:pointer; background:transparent; color:var(--ink3); transition:all .2s; text-align:center; }
  .upload-tab.active { background:var(--white); color:var(--ink); border:1px solid var(--paper3); }
  .drop-zone { border:1.5px dashed var(--paper3); border-radius:11px; padding:20px 14px; text-align:center; cursor:pointer; transition:all .2s; position:relative; }
  .drop-zone:hover, .drop-zone.drag-over { border-color:var(--ink); background:rgba(10,10,15,.03); }
  .drop-zone input[type=file] { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }
  .drop-icon { font-size:22px; margin-bottom:6px; }
  .drop-title { font-family:'Syne',sans-serif; font-size:12px; font-weight:700; color:var(--ink2); margin-bottom:3px; }
  .drop-sub { font-size:10px; color:var(--ink3); }
  .upload-item { display:flex; align-items:center; gap:9px; padding:8px 10px; background:var(--paper); border-radius:8px; border:1px solid var(--paper3); margin-top:7px; }
  .upload-thumb { width:40px; height:30px; border-radius:5px; object-fit:cover; flex-shrink:0; }
  .upload-thumb-icon { width:40px; height:30px; border-radius:5px; background:var(--paper2); display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
  .upload-info { flex:1; min-width:0; }
  .upload-name { font-size:11px; font-weight:600; color:var(--ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .upload-meta { font-size:9px; color:var(--ink3); display:flex; align-items:center; gap:6px; margin-top:1px; }
  .upload-status-ok { color:var(--green); font-weight:700; }
  .upload-status-err { color:var(--accent); font-weight:700; }
  .upload-progress { height:3px; background:var(--paper3); border-radius:99px; margin-top:4px; overflow:hidden; }
  .upload-progress-fill { height:100%; border-radius:99px; background:var(--ink); transition:width .3s; }
  .btn-rm-upload { width:22px; height:22px; background:#fee2e2; border:none; border-radius:5px; cursor:pointer; font-size:9px; flex-shrink:0; display:flex; align-items:center; justify-content:center; color:#dc2626; }
  .login-wrap { display:flex; align-items:center; justify-content:center; min-height:calc(100vh - var(--header-h)); padding:32px 16px; }
  .login-card { background:var(--white); border:1.5px solid var(--paper3); border-radius:24px; padding:40px 32px; width:100%; max-width:380px; animation:slideUp .35s var(--ease); }
  .login-icon { width:56px; height:56px; background:var(--ink); border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin:0 auto 20px; }
  .login-title { font-family:'Syne',sans-serif; font-size:22px; font-weight:800; text-align:center; margin-bottom:5px; }
  .login-sub   { color:var(--ink3); text-align:center; font-size:13px; margin-bottom:28px; }
  .login-form  { display:flex; flex-direction:column; gap:12px; }
  .login-err   { background:#fee2e2; color:#dc2626; padding:9px 13px; border-radius:9px; font-size:12px; font-weight:500; }
  .btn-login   { padding:13px; background:var(--ink); color:white; border:none; border-radius:12px; font-family:'Syne',sans-serif; font-size:13px; font-weight:700; cursor:pointer; transition:all .2s; }
  .btn-login:hover { background:var(--accent); }
  .toast { position:fixed; bottom:calc(var(--bottom-h) + 12px); right:16px; z-index:9999; background:var(--ink); color:white; padding:12px 16px; border-radius:12px; font-family:'Syne',sans-serif; font-size:12px; font-weight:700; display:flex; align-items:center; gap:8px; box-shadow:0 12px 32px rgba(0,0,0,.22); animation:toastIn .25s var(--ease); }
  @keyframes toastIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  .toast.success { border-left:4px solid var(--green); }
  .toast.error   { border-left:4px solid var(--accent); }
  .confirm-box { position:relative; background:var(--white); border-radius:18px; padding:24px; max-width:340px; width:100%; z-index:1; animation:slideUp .25s var(--ease); text-align:center; }
  .confirm-box h4 { font-family:'Syne',sans-serif; font-size:17px; font-weight:800; margin-bottom:6px; }
  .confirm-box p  { color:var(--ink3); font-size:13px; margin-bottom:20px; }
  .confirm-acts   { display:flex; gap:8px; }
  .btn-cf-del  { flex:1; padding:11px; background:#dc2626; color:white; border:none; border-radius:10px; font-family:'Syne',sans-serif; font-size:12px; font-weight:700; cursor:pointer; }
  .btn-cf-can  { padding:11px 18px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:10px; font-family:'Syne',sans-serif; font-size:12px; font-weight:700; color:var(--ink2); cursor:pointer; }
  .admin-tabs { display:flex; gap:2px; padding:16px 24px 0; background:var(--ink); }
  .admin-tab-btn { padding:8px 18px; border-radius:8px 8px 0 0; font-family:'Syne',sans-serif; font-size:11px; font-weight:700; border:none; cursor:pointer; background:rgba(255,255,255,.1); color:rgba(255,255,255,.6); transition:all .2s; }
  .admin-tab-btn.active { background:var(--paper); color:var(--ink); }
  .course-manager { padding:24px; }
  .course-add-form { display:flex; gap:10px; flex-wrap:wrap; align-items:flex-end; padding:16px; background:var(--paper); border-radius:14px; border:1.5px solid var(--paper3); margin-bottom:20px; }
  .course-add-form .f-group { flex:1; min-width:140px; }
  .course-add-form .f-label { font-family:'Syne',sans-serif; font-size:9px; font-weight:800; letter-spacing:1.2px; text-transform:uppercase; color:var(--ink3); display:block; margin-bottom:5px; }
  .btn-add-course { padding:10px 20px; background:var(--ink); color:white; border:none; border-radius:10px; font-family:'Syne',sans-serif; font-size:12px; font-weight:700; cursor:pointer; transition:all .2s; white-space:nowrap; align-self:flex-end; }
  .btn-add-course:hover { background:var(--green); }
  .courses-list { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:10px; }
  .course-item { display:flex; align-items:center; gap:12px; padding:12px 14px; background:var(--white); border:1.5px solid var(--paper3); border-radius:12px; transition:border-color .2s; }
  .course-item:hover { border-color:var(--ink3); }
  .course-color-dot { width:28px; height:28px; border-radius:8px; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; }
  .course-item-info { flex:1; min-width:0; }
  .course-item-label { font-family:'Syne',sans-serif; font-size:13px; font-weight:700; color:var(--ink); }
  .course-item-value { font-size:10px; color:var(--ink3); font-family:'DM Sans',sans-serif; }
  .course-item-default { font-size:9px; background:var(--paper2); color:var(--ink3); padding:2px 7px; border-radius:99px; font-weight:700; font-family:'Syne',sans-serif; }
  .course-item-acts { display:flex; gap:5px; }
  .color-picker-wrap { position:relative; }
  .color-swatch { width:32px; height:32px; border-radius:8px; border:2px solid var(--paper3); cursor:pointer; transition:border-color .2s; }
  .color-swatch:hover { border-color:var(--ink); }
  .color-dropdown { position:absolute; top:38px; left:0; z-index:100; background:var(--white); border:1.5px solid var(--paper3); border-radius:12px; padding:8px; display:grid; grid-template-columns:repeat(4,1fr); gap:5px; box-shadow:0 8px 24px rgba(0,0,0,.12); min-width:140px; }
  .color-opt { width:28px; height:28px; border-radius:7px; border:2px solid transparent; cursor:pointer; }
  .color-opt:hover { transform:scale(1.15); border-color:var(--ink); }
  .course-inline-edit { display:flex; gap:6px; flex:1; align-items:center; }
  .course-inline-edit input { flex:1; padding:6px 10px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:8px; font-family:'DM Sans',sans-serif; font-size:13px; color:var(--ink); outline:none; }
  .course-inline-edit input:focus { border-color:var(--ink); }
  .btn-save-course { padding:6px 12px; background:var(--green); color:white; border:none; border-radius:7px; font-family:'Syne',sans-serif; font-size:10px; font-weight:700; cursor:pointer; }
  .btn-cancel-course { padding:6px 10px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:7px; font-family:'Syne',sans-serif; font-size:10px; font-weight:700; color:var(--ink2); cursor:pointer; }
  .portal-login-page { min-height:100vh; display:flex; align-items:center; justify-content:center; background:var(--paper); padding:24px 16px; position:relative; overflow:hidden; }
  .portal-login-bg { position:absolute; inset:0; pointer-events:none; overflow:hidden; }
  .portal-login-blob { position:absolute; border-radius:50%; filter:blur(80px); opacity:.15; }
  .portal-login-card { position:relative; z-index:1; background:var(--white); border:1.5px solid var(--paper3); border-radius:28px; padding:44px 40px 40px; width:100%; max-width:420px; box-shadow:0 24px 64px rgba(10,10,15,.10); animation:slideUp .4s var(--ease); }
  .portal-login-brand { display:flex; flex-direction:column; align-items:center; margin-bottom:28px; }
  .portal-login-logo { width:68px; height:68px; background:var(--ink); border-radius:20px; display:flex; align-items:center; justify-content:center; font-family:'Syne',sans-serif; font-size:28px; font-weight:800; color:var(--paper); margin-bottom:16px; box-shadow:0 8px 24px rgba(10,10,15,.18); }
  .portal-login-brand h1 { font-family:'Syne',sans-serif; font-size:26px; font-weight:800; letter-spacing:-.6px; color:var(--ink); margin-bottom:6px; }
  .portal-login-brand h1 span { color:var(--accent); }
  .portal-login-brand p { font-size:13px; color:var(--ink3); text-align:center; line-height:1.5; max-width:280px; }
  .portal-login-divider { height:1px; background:var(--paper2); margin:0 0 26px; }
  .portal-login-form { display:flex; flex-direction:column; gap:12px; }
  .portal-login-input-wrap { position:relative; }
  .portal-login-input-wrap svg { position:absolute; left:13px; top:50%; transform:translateY(-50%); color:var(--ink3); pointer-events:none; }
  .portal-login-input { width:100%; padding:13px 13px 13px 42px; background:var(--paper); border:1.5px solid var(--paper3); border-radius:13px; font-family:'DM Sans',sans-serif; font-size:15px; color:var(--ink); outline:none; transition:all .2s; }
  .portal-login-input:focus { border-color:var(--ink); box-shadow:0 0 0 3px rgba(10,10,15,.06); }
  .portal-login-input::placeholder { color:var(--ink3); }
  .portal-login-err { background:#fee2e2; color:#dc2626; border:1px solid #fecaca; padding:10px 14px; border-radius:10px; font-size:12px; font-weight:600; display:flex; align-items:center; gap:7px; }
  .portal-login-btn { width:100%; padding:14px; background:var(--ink); color:white; border:none; border-radius:13px; font-family:'Syne',sans-serif; font-size:14px; font-weight:800; cursor:pointer; transition:all .2s; letter-spacing:-.2px; display:flex; align-items:center; justify-content:center; gap:8px; }
  .portal-login-btn:hover { background:var(--accent); }
  .portal-login-btn:active { transform:scale(.98); }
  .portal-login-footer { margin-top:22px; text-align:center; font-size:11px; color:var(--ink3); line-height:1.7; }
  .portal-login-pills { display:flex; flex-wrap:wrap; gap:6px; justify-content:center; margin-top:16px; }
  .portal-login-pill { padding:4px 12px; border-radius:99px; border:1.5px solid var(--paper3); font-size:10px; font-weight:700; font-family:'Syne',sans-serif; color:var(--ink3); background:var(--paper); }
  @media (max-width:480px) { .portal-login-card { padding:32px 22px 28px; border-radius:20px; } .portal-login-brand h1 { font-size:22px; } }
  @media (max-width:768px) {
    :root { --bottom-h:60px; }
    .tab-group { display:none; }
    .filter-toggle-btn { display:flex; }
    .bottom-tabs { display:flex; }
    .layout { padding:12px 12px calc(var(--bottom-h) + 20px); flex-direction:column; gap:12px; }
    .sidebar-wrap { display:none; }
    .card-grid { grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:10px; }
    .card-img  { height:140px; }
    .card-name { font-size:13px; min-height:36px; }
    .card-body { padding:10px 12px 12px; }
    .modal-box { flex-direction:column; max-height:96vh; border-radius:20px 20px 0 0; position:fixed; bottom:0; left:0; right:0; max-width:100%; }
    .modal-media { width:100%; height:200px; flex-shrink:0; }
    .modal-info { padding:16px 16px 20px; }
    .modal-title { font-size:17px; }
    .admin-panel { margin:12px; }
    .toast { left:16px; right:16px; bottom:calc(var(--bottom-h) + 10px); }
    .contact-grid { grid-template-columns:1fr; }
    .hostel-row-grid { grid-template-columns:1fr 1fr auto auto; }
  }
  @media (max-width:420px) {
    .card-grid { grid-template-columns:1fr 1fr; }
    .header { padding:0 12px; }
    .logo-text { display:none; }
  }
`;

// ─── HELPERS ─────────────────────────────────────────────────────────
function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2); }
function ytEmbed(url) {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}
function calcTotal(years) { return calcTotalAmt(years); }
function fmtBytes(b) {
  if (b < 1024) return b + "B";
  if (b < 1048576) return (b/1024).toFixed(1) + "KB";
  return (b/1048576).toFixed(1) + "MB";
}
function fileIcon(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  if (["jpg","jpeg","png","webp","gif"].includes(ext)) return null;
  if (ext === "pdf") return "📄";
  if (["xlsx","xls"].includes(ext)) return "📊";
  if (ext === "csv") return "📋";
  return "📁";
}
function normalizeVideos(u) {
  if (!u) return u;
  if (u.videos && Array.isArray(u.videos)) return u;
  const videos = [];
  if (u.video && u.video.trim()) {
    videos.push({ id: uid(), label: "Campus Video", url: ytEmbed(u.video) || u.video });
  }
  const { video, ...rest } = u;
  return { ...rest, videos };
}

// ─── PHP API CALLS ────────────────────────────────────────────────────
async function apiGet() {
  const res = await fetch(`${API_URL}?action=get`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load data");
  return json.data;
}

async function apiSaveUniversity(university) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "save_university", university })
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Save failed");
  return json;
}

async function apiDeleteUniversity(id) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "delete_university", id })
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Delete failed");
  return json;
}

async function apiSaveCourses(courses) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "save_courses", courses })
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to save courses");
  return json;
}

async function apiSeed(universities, courses) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "seed", universities, courses })
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Seed failed");
  return json;
}

// ─── UPLOAD FIELD ────────────────────────────────────────────────────
function UploadField({ accept, multiple, label, icon, hint, value, onChange }) {
  const [tab, setTab] = useState("upload");
  const [items, setItems] = useState(() => {
    if (!value) return [];
    const arr = Array.isArray(value) ? value : [value];
    return arr.filter(Boolean).map(url => ({
      id: uid(), url, name: url.split("/").pop().split("?")[0] || url,
      status: "done", progress: 100, preview: url
    }));
  });
  const [urlInput, setUrlInput] = useState(
    Array.isArray(value) ? "" : (value && value !== "#" ? value : "")
  );
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();

  const notifyParent = useCallback((newItems) => {
    const urls = newItems.filter(i => i.status === "done" && i.url).map(i => i.url);
    if (multiple) onChange(urls);
    else onChange(urls[0] || "");
  }, [multiple, onChange]);

  const doUpload = async (file) => {
    const itemId = uid();
    const preview = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
    const newItem = { id: itemId, name: file.name, size: file.size, status: "uploading", progress: 0, preview, url: "" };
    setItems(prev => multiple ? [...prev, newItem] : [newItem]);
    try {
      const formData = new FormData();
      formData.append("file", file);
      let fakeProgress = 0;
      const ticker = setInterval(() => {
        fakeProgress = Math.min(fakeProgress + 15, 85);
        setItems(prev => prev.map(i => i.id === itemId ? { ...i, progress: fakeProgress } : i));
      }, 200);
      const res = await fetch(UPLOAD_URL, { method: "POST", body: formData });
      clearInterval(ticker);
      const data = await res.json();
      if (data.url) {
        setItems(prev => {
          const updated = prev.map(i => i.id === itemId ? { ...i, status: "done", progress: 100, url: data.url } : i);
          notifyParent(updated);
          return updated;
        });
      } else { throw new Error(data.error || "Upload failed"); }
    } catch (err) {
      console.error("Upload error:", err);
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, status: "error", progress: 0 } : i));
    }
  };

  const handleFiles = (files) => Array.from(files).forEach(f => doUpload(f));
  const removeItem = (id) => {
    setItems(prev => {
      const updated = prev.filter(i => i.id !== id);
      notifyParent(updated);
      return updated;
    });
  };
  const applyUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    const newItem = { id: uid(), name: url.split("/").pop().split("?")[0] || "link", status: "done", progress: 100, preview: url.match(/\.(jpg|jpeg|png|webp|gif)/i) ? url : null, url };
    setItems(prev => {
      const updated = multiple ? [...prev, newItem] : [newItem];
      notifyParent(updated);
      return updated;
    });
    setUrlInput("");
  };

  return (
    <div>
      <div className="upload-tabs">
        <button className={`upload-tab${tab === "upload" ? " active" : ""}`} onClick={() => setTab("upload")}>⬆ Upload File</button>
        <button className={`upload-tab${tab === "url" ? " active" : ""}`} onClick={() => setTab("url")}>🔗 Paste URL</button>
      </div>
      {tab === "upload" && (
        <div className={`drop-zone${dragging ? " drag-over" : ""}`}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}>
          <input ref={fileRef} type="file" accept={accept} multiple={multiple} onChange={e => handleFiles(e.target.files)} />
          <div className="drop-icon">{icon}</div>
          <div className="drop-title">Drop {label} here or click to browse</div>
          <div className="drop-sub">{hint}</div>
        </div>
      )}
      {tab === "url" && (
        <div style={{ display: "flex", gap: 7 }}>
          <input className="f-input" placeholder={accept.includes("image") ? "https://images.unsplash.com/..." : "https://drive.google.com/..."} value={urlInput} onChange={e => setUrlInput(e.target.value)} onKeyDown={e => e.key === "Enter" && applyUrl()} style={{ flex: 1 }} />
          <button onClick={applyUrl} style={{ padding: "10px 14px", background: "var(--ink)", color: "white", border: "none", borderRadius: 10, fontFamily: "Syne,sans-serif", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>Add →</button>
        </div>
      )}
      {items.map(item => {
        const ico = fileIcon(item.name);
        return (
          <div className="upload-item" key={item.id}>
            {ico ? <div className="upload-thumb-icon">{ico}</div>
              : item.preview ? <img className="upload-thumb" src={item.preview} alt="" onError={e => { e.target.style.display = "none"; }} />
              : <div className="upload-thumb-icon">📁</div>}
            <div className="upload-info">
              <div className="upload-name">{item.name}</div>
              <div className="upload-meta">
                {item.size && <span>{fmtBytes(item.size)}</span>}
                {item.status === "uploading" && <span>↑ {item.progress}%</span>}
                {item.status === "done" && <span className="upload-status-ok">✓ Ready</span>}
                {item.status === "error" && <span className="upload-status-err">✗ Failed</span>}
              </div>
              {item.status === "uploading" && <div className="upload-progress"><div className="upload-progress-fill" style={{ width: item.progress + "%" }} /></div>}
            </div>
            <button className="btn-rm-upload" onClick={() => removeItem(item.id)}>✕</button>
          </div>
        );
      })}
    </div>
  );
}

// ─── PORTAL LOGIN ─────────────────────────────────────────────────────
function PortalLogin({ onAuth }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const [show, setShow] = useState(false);
  const attempt = (e) => {
    e.preventDefault();
    if (pw === PORTAL_PASSWORD || pw === ADMIN_PASSWORD) {
      onAuth(pw === ADMIN_PASSWORD ? "admin" : "user");
    } else { setErr(true); setPw(""); }
  };
  return (
    <>
      <style>{STYLES}</style>
      <div className="portal-login-page">
        <div className="portal-login-bg">
          <div className="portal-login-blob" style={{width:400,height:400,background:"#dbeafe",top:"-120px",left:"-100px"}}/>
          <div className="portal-login-blob" style={{width:300,height:300,background:"#fce7f3",bottom:"-80px",right:"-60px"}}/>
          <div className="portal-login-blob" style={{width:200,height:200,background:"#fef3c7",top:"40%",right:"10%"}}/>
        </div>
        <div className="portal-login-card">
          <div className="portal-login-brand">
            <div className="portal-login-logo">U</div>
            <h1>UNI<span>PORTAL</span></h1>
            <p>Your complete guide to universities abroad and across India</p>
          </div>
          <div className="portal-login-divider"/>
          <form className="portal-login-form" onSubmit={attempt}>
            {err && <div className="portal-login-err"><span>✕</span> Incorrect password. Please try again.</div>}
            <div className="portal-login-input-wrap">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <input className="portal-login-input" type={show ? "text" : "password"} placeholder="Enter access password…" value={pw} onChange={e => { setPw(e.target.value); setErr(false); }} autoFocus />
            </div>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"2px 4px"}}>
              <input type="checkbox" checked={show} onChange={e=>setShow(e.target.checked)} style={{width:14,height:14,cursor:"pointer"}}/>
              <span style={{fontSize:12,color:"var(--ink3)"}}>Show password</span>
            </label>
            <button className="portal-login-btn" type="submit">
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              Enter Portal
            </button>
          </form>
          <div className="portal-login-footer">
            <div>🌍 Abroad universities &nbsp;·&nbsp; 🇮🇳 Domestic colleges</div>
            <div className="portal-login-pills">
              {["MBBS","MBA","Nursing","Engineering","Pharmacy","BPT / BOT"].map(p => <span key={p} className="portal-login-pill">{p}</span>)}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── TOAST ───────────────────────────────────────────────────────────
function Toast({ msg, type, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2600); return () => clearTimeout(t); }, [onDone]);
  return <div className={`toast ${type}`}>{type === "success" ? "✓" : "✕"} {msg}</div>;
}

// ─── CONFIRM ─────────────────────────────────────────────────────────
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="modal-backdrop" style={{ zIndex: 400 }}>
      <div className="modal-overlay" onClick={onCancel} />
      <div className="confirm-box">
        <div style={{ fontSize: 32, marginBottom: 10 }}>🗑️</div>
        <h4>Delete University?</h4>
        <p>"{name}" will be permanently removed.</p>
        <div className="confirm-acts">
          <button className="btn-cf-can" onClick={onCancel}>Cancel</button>
          <button className="btn-cf-del" onClick={onConfirm}>Yes, Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── FEE DISPLAY ─────────────────────────────────────────────────────
function FeeDisplay({ feeStructures, uniType }) {
  const fees = feeStructures || [];
  const [activeIdx, setActiveIdx] = useState(0);
  if (!fees.length) return null;
  const active = fees[activeIdx] || fees[0];
  const currency = active.currency || (uniType === "abroad" ? "USD" : "INR");
  const sym = CURRENCY_SYM[currency] || "₹";
  const feeMode = active.feeMode || "year";
  const isSimple = feeMode === "simple";
  const visibleYears = (active.years || []).filter(y => !y.hidden);
  const total = !isSimple && visibleYears.length ? calcTotal(visibleYears) : null;
  const hostelRows = (active.hostelRows || []).filter(r => !r.hidden);
  const periodLabel = { year:"Year", semester:"Semester", term:"Term", simple:"Total" }[feeMode] || "Period";
  const hostelGrandTotal = hostelRows.length ? (() => {
    const nums = hostelRows.map(r => { const amt = parseInt((r.amount||"").replace(/[^0-9]/g,"")); const mul = parseInt(r.multiplier||1); return isNaN(amt) ? NaN : amt*mul; }).filter(n => !isNaN(n));
    if (!nums.length) return null;
    return nums.reduce((a,b) => a+b, 0).toLocaleString("en-IN");
  })() : null;

  return (
    <div className="fee-section">
      <div className="fee-section-title">
        <span style={{ display:"flex", alignItems:"center", gap:6 }}>
          💰 Fee Structure
          {uniType === "abroad" && <span style={{ fontSize:9, fontWeight:700, padding:"2px 8px", background: currency==="USD" ? "#dbeafe" : "#dcfce7", color: currency==="USD" ? "#1d4ed8" : "#15803d", borderRadius:99 }}>{currency==="USD" ? "$ USD" : "₹ INR"}</span>}
        </span>
      </div>
      {fees.length > 1 && (
        <div className="fee-tabs">
          {fees.map((f, i) => (
            <button key={i} className={`fee-tab${i===activeIdx?" active":""}`} onClick={() => setActiveIdx(i)}>
              {f.label || CLBL[f.course] || f.course}
              {f.seats && f.seats.trim() && <span style={{ marginLeft:5, fontSize:9, background:"#dcfce7", color:"#15803d", padding:"1px 6px", borderRadius:99, fontWeight:700 }}>{f.seats}</span>}
            </button>
          ))}
        </div>
      )}
      {active.seats && active.seats.trim() && (
        <div className="fee-seats-badge"><span className="fee-seats-dot"/><span>{active.seats} seats available</span></div>
      )}
      {isSimple ? (
        active.totalFee ? (
          <div style={{ background:"var(--paper)", border:"1.5px solid var(--paper3)", borderRadius:12, padding:"14px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontSize:12, color:"var(--ink3)", fontWeight:600 }}>Total Fee</span>
            <span style={{ fontFamily:"Syne,sans-serif", fontWeight:800, fontSize:18, color:"var(--ink)" }}>{sym} {active.totalFee}</span>
          </div>
        ) : null
      ) : (
        visibleYears.length > 0 && (
          <div className="fee-table-wrap">
            <table className="fee-table">
              <thead><tr><th>{periodLabel}</th><th>Fee ({sym})</th></tr></thead>
              <tbody>
                {visibleYears.map((y,i) => <tr key={i}><td className="fee-year">{y.label}</td><td className="fee-amount">{sym} {y.amount}</td></tr>)}
                {total && <tr className="fee-total-row"><td>Total Cost</td><td className="fee-total-amt">{sym} {total}</td></tr>}
              </tbody>
            </table>
          </div>
        )
      )}
      {hostelRows.length > 0 && (
        <div className="hostel-section">
          <div className="hostel-section-title">🏠 Hostel / Accommodation Fee</div>
          <div className="fee-table-wrap">
            <table className="hostel-table">
              <thead><tr><th>Description</th><th>Per Year ({sym})</th><th>Duration</th><th>Total ({sym})</th></tr></thead>
              <tbody>
                {hostelRows.map((r,i) => { const rowTotal = calcHostelRowTotal(r); const mul = parseInt(r.multiplier||1); return <tr key={i}><td className="fee-year">{r.label||"Hostel Fee"}</td><td className="fee-amount">{sym} {r.amount}</td><td><span className="hostel-mul-chip">×{mul} {mul===1?"yr":"yrs"}</span></td><td className="fee-amount">{rowTotal ? `${sym} ${rowTotal}` : "—"}</td></tr>; })}
                {hostelGrandTotal && <tr className="hostel-total-row"><td colSpan={3}>Total Hostel Cost</td><td className="fee-total-amt">{sym} {hostelGrandTotal}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {(isSimple ? active.totalFee : visibleYears.length > 0) && <p className="fee-note">* Fees are approximate and subject to change.</p>}
      {(active.feeFileUrl || (active.brochureLink && active.brochureLink !== "#")) && (
        <div className="fee-download-row">
          {active.feeFileUrl && <a href={active.feeFileUrl} target="_blank" rel="noreferrer" className="fee-brochure-link fee-sheet-btn">📎 Download Fee Sheet</a>}
          {active.brochureLink && active.brochureLink !== "#" && <a href={active.brochureLink} target="_blank" rel="noreferrer" className="fee-brochure-link brochure-btn">📄 Download {active.label} Brochure</a>}
        </div>
      )}
    </div>
  );
}

// ─── CONTACT DISPLAY ─────────────────────────────────────────────────
function ContactDisplay({ contact }) {
  const c = contact || {};
  const hasAny = c.name || c.phone || c.email || c.whatsapp;
  return (
    <div className="contact-section">
      <div className="contact-section-title">📞 Point of Contact</div>
      {!hasAny ? <div className="contact-na">Contact details not available.</div> : (
        <div className="contact-grid">
          {c.name && <div className="contact-item"><div className="contact-item-icon nm">👤</div><div className="contact-item-body"><div className="contact-item-lbl">Name</div><div className="contact-item-val">{c.name}</div></div></div>}
          {c.phone && <a className="contact-item" href={`tel:${c.phone.replace(/\s/g,"")}`}><div className="contact-item-icon ph">📱</div><div className="contact-item-body"><div className="contact-item-lbl">Phone</div><div className="contact-item-val">{c.phone}</div></div></a>}
          {c.email && <a className="contact-item" href={`mailto:${c.email}`} style={{gridColumn: c.phone&&c.name?"1/-1":"auto"}}><div className="contact-item-icon em">✉️</div><div className="contact-item-body"><div className="contact-item-lbl">Email</div><div className="contact-item-val">{c.email}</div></div></a>}
          {c.whatsapp && <a className="contact-item" href={`https://wa.me/${c.whatsapp.replace(/[^0-9]/g,"")}`} target="_blank" rel="noreferrer"><div className="contact-item-icon wa">💬</div><div className="contact-item-body"><div className="contact-item-lbl">WhatsApp</div><div className="contact-item-val">{c.whatsapp}</div></div></a>}
        </div>
      )}
    </div>
  );
}

// ─── FEE EDITOR ──────────────────────────────────────────────────────
function FeeEditor({ courses, feeStructures, onChange, uniType }) {
  const fees = feeStructures || [];
  useEffect(() => {
    const activeCourses = courses.filter(c => c !== "multi");
    const updated = activeCourses.map(c => {
      const existing = fees.find(f => f.course === c);
      return existing || mkFee(c, CLBL[c] || c, [], uniType === "abroad" ? "USD" : "INR", "year");
    });
    onChange(updated);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses.join(","), uniType]);

  const updateFee = (idx, newFee) => onChange(fees.map((f, i) => i === idx ? newFee : f));
  const changeFeeMode = (fIdx, mode) => {
    const f = fees[fIdx];
    if (mode === "simple") { updateFee(fIdx, { ...f, feeMode:"simple", years:[], totalFee: f.totalFee||"" }); }
    else { const existingRows = (f.years&&f.years.length>0) ? f.years : [{ label:`${mode.charAt(0).toUpperCase()+mode.slice(1)} 1`, amount:"" }]; updateFee(fIdx, { ...f, feeMode:mode, years:existingRows }); }
  };
  const updateYear = (fIdx, yIdx, key, val) => { const f = { ...fees[fIdx], years: fees[fIdx].years.map((y,i) => i===yIdx ? {...y,[key]:val} : y) }; updateFee(fIdx, f); };
  const addRow = (fIdx) => { const f = fees[fIdx]; const mode = f.feeMode||"year"; const count = (f.years||[]).length+1; const lbl = { year:`Year ${count}`, semester:`Semester ${count}`, term:`Term ${count}` }; updateFee(fIdx, { ...f, years:[...(f.years||[]), { label:lbl[mode]||`Row ${count}`, amount:"", hidden:false }] }); };
  const rmRow = (fIdx, yIdx) => updateFee(fIdx, { ...fees[fIdx], years: fees[fIdx].years.filter((_,i) => i!==yIdx) });
  const addHostelRow = (fIdx) => { const f = fees[fIdx]; const rows = f.hostelRows||[]; updateFee(fIdx, { ...f, hostelRows:[...rows,{id:uid(),label:"Hostel Fee",amount:"",multiplier:"1",hidden:false}] }); };
  const updateHostelRow = (fIdx, rIdx, key, val) => { const f = { ...fees[fIdx], hostelRows:(fees[fIdx].hostelRows||[]).map((r,i) => i===rIdx?{...r,[key]:val}:r) }; updateFee(fIdx, f); };
  const rmHostelRow = (fIdx, rIdx) => updateFee(fIdx, { ...fees[fIdx], hostelRows:(fees[fIdx].hostelRows||[]).filter((_,i) => i!==rIdx) });

  if (!fees.length) return <p style={{ fontSize:12, color:"var(--ink3)" }}>Select courses above to add fee structures.</p>;

  return (
    <div className="fee-editor">
      {fees.map((f, fIdx) => {
        const feeMode = f.feeMode || "year";
        const isSimple = feeMode === "simple";
        const currency = f.currency || (uniType === "abroad" ? "USD" : "INR");
        const sym = CURRENCY_SYM[currency] || "₹";
        const isAbroad = uniType === "abroad";
        const hostelRows = f.hostelRows || [];

        return (
          <div className="fee-editor-card" key={f.course}>
            <div className="fee-editor-head">
              <span>💰 {f.label || CLBL[f.course] || f.course}</span>
              {isAbroad && (
                <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                  {["INR","USD"].map(cur => (
                    <button key={cur} onClick={() => updateFee(fIdx, {...f, currency:cur})}
                      style={{ padding:"3px 10px", borderRadius:7, border:"1.5px solid", fontFamily:"Syne,sans-serif", fontSize:10, fontWeight:700, cursor:"pointer", background:currency===cur?"var(--ink)":"var(--paper)", color:currency===cur?"white":"var(--ink3)", borderColor:currency===cur?"var(--ink)":"var(--paper3)", transition:"all .15s" }}>
                      {cur==="INR"?"₹ INR":"$ USD"}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ padding:"10px 14px 0", display:"flex", flexWrap:"wrap", gap:5 }}>
              {FEE_MODES.map(m => (
                <button key={m.value} onClick={() => changeFeeMode(fIdx, m.value)}
                  style={{ padding:"5px 12px", borderRadius:8, border:"1.5px solid", fontFamily:"Syne,sans-serif", fontSize:10, fontWeight:700, cursor:"pointer", background:feeMode===m.value?"var(--ink)":"var(--paper)", color:feeMode===m.value?"white":"var(--ink3)", borderColor:feeMode===m.value?"var(--ink)":"var(--paper3)", transition:"all .15s" }}>
                  {m.icon} {m.label}
                </button>
              ))}
            </div>
            <div className="fee-editor-body">
              <div style={{ paddingBottom:8, borderBottom:"1px solid var(--paper3)", marginBottom:2 }}>
                <div style={{ fontSize:9, fontWeight:800, letterSpacing:1.2, textTransform:"uppercase", color:"var(--ink3)", marginBottom:6 }}>🪑 Seats Available</div>
                <input className="f-input" style={{ padding:"8px 12px", fontSize:13 }} placeholder='e.g. 45 or "Limited"' value={f.seats||""} onChange={e => updateFee(fIdx, {...f, seats:e.target.value})} />
              </div>

              {isSimple ? (
                <div className="f-group">
                  <label className="f-label">Total Fee ({sym})</label>
                  <div style={{ position:"relative" }}>
                    <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:13, fontWeight:700, color:"var(--ink3)", pointerEvents:"none" }}>{sym}</span>
                    <input className="f-input" style={{ paddingLeft:26 }} placeholder="e.g. 15,00,000" value={f.totalFee||""} onChange={e => updateFee(fIdx, {...f, totalFee:e.target.value})} />
                  </div>
                </div>
              ) : (
                <>
                  {(f.years||[]).map((y, yIdx) => (
                    <div key={yIdx} style={{ display:"flex", flexDirection:"column", gap:2 }}>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:7, alignItems:"center", opacity:y.hidden?0.45:1 }}>
                        <input style={{ padding:"7px 10px", background:"var(--white)", border:"1.5px solid var(--paper3)", borderRadius:8, fontFamily:"DM Sans,sans-serif", fontSize:12, color:"var(--ink)", outline:"none", width:"100%" }} value={y.label} onChange={e => updateYear(fIdx, yIdx, "label", e.target.value)} />
                        <div style={{ position:"relative", display:"flex", alignItems:"center" }}>
                          <span style={{ position:"absolute", left:9, fontSize:12, fontWeight:700, color:"var(--ink3)", pointerEvents:"none", zIndex:1 }}>{sym}</span>
                          <input className="fee-amount-input" placeholder="e.g. 1,50,000" value={y.amount} disabled={y.hidden} onChange={e => updateYear(fIdx, yIdx, "amount", e.target.value)} />
                        </div>
                        <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                          <button onClick={() => updateYear(fIdx, yIdx, "hidden", !y.hidden)} style={{ width:30, height:28, borderRadius:6, border:"1.5px solid", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", background:y.hidden?"#f3e8ff":"#f0fdf4", color:y.hidden?"#7c3aed":"#16a34a", borderColor:y.hidden?"#d8b4fe":"#86efac", transition:"all .15s", flexShrink:0 }}>{y.hidden?"🙈":"👁"}</button>
                          {(f.years||[]).length > 1 && <button className="btn-rm-year" onClick={() => rmRow(fIdx, yIdx)}>✕</button>}
                        </div>
                      </div>
                    </div>
                  ))}
                  <button className="btn-add-year" onClick={() => addRow(fIdx)}>+ Add {{ year:"Year", semester:"Semester", term:"Term" }[feeMode]||"Row"}</button>
                </>
              )}

              <div className="hostel-editor-wrap">
                <div style={{ fontSize:9, fontWeight:800, letterSpacing:1.2, textTransform:"uppercase", color:"#0369a1", marginBottom:8 }}>
                  <span style={{ background:"#e0f2fe", color:"#0369a1", padding:"3px 9px", borderRadius:6, border:"1px solid #bae6fd" }}>🏠 Hostel / Accommodation Fee</span>
                </div>
                {hostelRows.map((r, rIdx) => (
                  <div key={r.id||rIdx} style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto auto", gap:7, alignItems:"center", marginBottom:6, opacity:r.hidden?0.45:1 }}>
                    <input style={{ padding:"7px 10px", background:"var(--white)", border:"1.5px solid var(--paper3)", borderRadius:8, fontFamily:"DM Sans,sans-serif", fontSize:12, color:"var(--ink)", outline:"none", width:"100%" }} placeholder="e.g. Hostel Fee" value={r.label} onChange={e => updateHostelRow(fIdx, rIdx, "label", e.target.value)} />
                    <div style={{ position:"relative", display:"flex", alignItems:"center" }}>
                      <span style={{ position:"absolute", left:9, fontSize:12, fontWeight:700, color:"var(--ink3)", pointerEvents:"none", zIndex:1 }}>{sym}</span>
                      <input className="fee-amount-input" placeholder="e.g. 80,000" value={r.amount} disabled={r.hidden} onChange={e => updateHostelRow(fIdx, rIdx, "amount", e.target.value)} />
                    </div>
                    <select className="hostel-mul-select" value={r.multiplier||"1"} disabled={r.hidden} onChange={e => updateHostelRow(fIdx, rIdx, "multiplier", e.target.value)}>
                      {[1,2,3,4,5,6,7].map(n => <option key={n} value={String(n)}>×{n}{n===1?" yr":" yrs"}</option>)}
                    </select>
                    <div style={{ display:"flex", gap:4 }}>
                      <button onClick={() => updateHostelRow(fIdx, rIdx, "hidden", !r.hidden)} style={{ width:28, height:28, borderRadius:6, border:"1.5px solid", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", background:r.hidden?"#f3e8ff":"#f0fdf4", color:r.hidden?"#7c3aed":"#16a34a", borderColor:r.hidden?"#d8b4fe":"#86efac" }}>{r.hidden?"🙈":"👁"}</button>
                      <button className="btn-rm-year" onClick={() => rmHostelRow(fIdx, rIdx)}>✕</button>
                    </div>
                  </div>
                ))}
                <button className="btn-add-year" style={{ borderColor:"#bae6fd", color:"#0369a1", marginTop:4 }} onClick={() => addHostelRow(fIdx)}>🏠 Add Hostel Row</button>
              </div>

              <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid var(--paper3)" }}>
                <div style={{ fontSize:9, fontWeight:800, letterSpacing:1.2, textTransform:"uppercase", color:"var(--ink3)", marginBottom:7 }}>📎 Fee Sheet File (optional)</div>
                <UploadField accept=".pdf,.xlsx,.xls,.csv" multiple={false} label="fee sheet" icon="📊" hint="PDF, Excel or CSV" value={f.feeFileUrl||""} onChange={url => updateFee(fIdx, {...f, feeFileUrl:url})} />
              </div>
              <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid var(--paper3)" }}>
                <div style={{ fontSize:9, fontWeight:800, letterSpacing:1.2, textTransform:"uppercase", color:"var(--ink3)", marginBottom:7 }}>📄 Course Brochure (optional)</div>
                <UploadField accept=".pdf" multiple={false} label="brochure PDF" icon="📋" hint="PDF only" value={f.brochureLink&&f.brochureLink!=="#"?f.brochureLink:""} onChange={url => updateFee(fIdx, {...f, brochureLink:url||"#"})} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── SINGLE VIDEO FIELD ───────────────────────────────────────────────
function SingleVideoField({ value, onChange }) {
  const [tab, setTab] = useState(value && !value.includes("youtube") ? "upload" : "youtube");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedUrl, setUploadedUrl] = useState(value && !value.includes("youtube.com/embed") ? value : "");
  const [ytInput, setYtInput] = useState(value && value.includes("youtube") ? value : "");
  const fileRef = useRef();
  const isYoutube = value && (value.includes("youtube.com/embed") || value.includes("youtube") || value.includes("youtu.be"));
  const isUploaded = value && !isYoutube && value !== "";

  const doUpload = async (file) => {
    setUploading(true); setProgress(0);
    try {
      const formData = new FormData(); formData.append("file", file);
      let fakeP = 0;
      const ticker = setInterval(() => { fakeP = Math.min(fakeP+10, 88); setProgress(fakeP); }, 300);
      const res = await fetch(UPLOAD_URL, { method:"POST", body:formData });
      clearInterval(ticker);
      const data = await res.json();
      if (data.url) { setUploadedUrl(data.url); onChange(data.url); setProgress(100); }
      else { throw new Error(data.error); }
    } catch(e) { console.error(e); alert("Video upload failed: " + e.message); }
    setUploading(false);
  };
  const applyYt = () => { const url = ytInput.trim(); if (!url) return; onChange(ytEmbed(url)||url); };
  const clear = () => { onChange(""); setUploadedUrl(""); setYtInput(""); setProgress(0); };

  return (
    <div>
      <div className="upload-tabs" style={{ marginBottom:10 }}>
        <button className={`upload-tab${tab==="upload"?" active":""}`} onClick={() => setTab("upload")}>⬆ Upload</button>
        <button className={`upload-tab${tab==="youtube"?" active":""}`} onClick={() => setTab("youtube")}>▶ YouTube</button>
      </div>
      {tab==="upload" && (
        <>
          {!isUploaded && (
            <div className="drop-zone" onClick={() => fileRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); const f=e.dataTransfer.files[0]; if(f) doUpload(f); }}>
              <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/ogg,video/mov" style={{ display:"none" }} onChange={e => { const f=e.target.files[0]; if(f) doUpload(f); }} />
              <div className="drop-icon">🎬</div>
              <div className="drop-title">Drop video here or click to browse</div>
              <div className="drop-sub">MP4, WebM, MOV — max 50MB</div>
            </div>
          )}
          {uploading && <div style={{ marginTop:8 }}><div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"var(--ink3)", marginBottom:4 }}><span>Uploading…</span><span>{progress}%</span></div><div className="upload-progress" style={{ height:5 }}><div className="upload-progress-fill" style={{ width:progress+"%" }}/></div></div>}
          {isUploaded && !uploading && <div className="video-preview"><video src={uploadedUrl} controls preload="metadata"/><button className="video-preview-rm" onClick={clear}>✕</button></div>}
        </>
      )}
      {tab==="youtube" && (
        <div>
          <div style={{ display:"flex", gap:7, marginBottom:8 }}>
            <input className="f-input" placeholder="https://youtube.com/watch?v=..." value={ytInput} onChange={e => setYtInput(e.target.value)} onKeyDown={e => e.key==="Enter"&&applyYt()} style={{ flex:1 }} />
            <button onClick={applyYt} style={{ padding:"10px 14px", background:"var(--ink)", color:"white", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>Set →</button>
          </div>
          {isYoutube && value && (
            <div style={{ position:"relative" }}>
              <iframe src={value.includes("embed")?value:ytEmbed(value)} style={{ width:"100%", aspectRatio:"16/9", borderRadius:10, border:"none", display:"block" }} allowFullScreen title="preview"/>
              <button className="video-preview-rm" onClick={clear}>✕</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MULTI-VIDEO EDITOR ───────────────────────────────────────────────
function VideosEditor({ videos, onChange }) {
  const vids = videos || [];
  const addVideo = () => onChange([...vids, { id:uid(), label:`Video ${vids.length+1}`, url:"" }]);
  const updateVideo = (idx, key, val) => onChange(vids.map((v,i) => i===idx ? {...v,[key]:val} : v));
  const removeVideo = (idx) => onChange(vids.filter((_,i) => i!==idx));
  return (
    <div>
      <div className="video-list">
        {vids.map((v, idx) => (
          <div className="video-item-card" key={v.id||idx}>
            <div className="video-item-head">
              <div style={{ display:"flex", alignItems:"center", gap:8, flex:1, minWidth:0 }}>
                <span>🎬</span>
                <input value={v.label||""} onChange={e => updateVideo(idx,"label",e.target.value)} placeholder={`Video ${idx+1} label`}
                  style={{ flex:1, padding:"4px 8px", background:"var(--white)", border:"1.5px solid var(--paper3)", borderRadius:7, fontFamily:"Syne,sans-serif", fontSize:11, fontWeight:700, color:"var(--ink2)", outline:"none", minWidth:0 }} />
              </div>
              <button onClick={() => removeVideo(idx)} style={{ marginLeft:8, width:24, height:24, background:"#fee2e2", border:"none", borderRadius:6, cursor:"pointer", fontSize:10, color:"#dc2626", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✕</button>
            </div>
            <div className="video-item-body">
              <SingleVideoField value={v.url} onChange={url => updateVideo(idx,"url",url)} />
            </div>
          </div>
        ))}
      </div>
      <button className="btn-add-video" onClick={addVideo}>🎬 Add Video</button>
    </div>
  );
}

// ─── FORM MODAL ──────────────────────────────────────────────────────
const BLANK = { type:"abroad", name:"", location:"", country:"", state:"tamilnadu", city:"coimbatore", category:"primary", courses:[], seats:"—", images:[], videos:[], brochure:"", feeStructures:[], hidden:false, contact: { ...BLANK_CONTACT } };

function FormModal({ initial, courseList, onSave, onClose }) {
  const [data, setData] = useState(() => ({ ...BLANK, contact:{...BLANK_CONTACT}, ...(initial||{}) }));
  const set = (k,v) => setData(d => ({...d,[k]:v}));
  const setContact = (k,v) => setData(d => ({...d, contact:{...(d.contact||{}),[k]:v}}));
  const toggle = c => set("courses", data.courses.includes(c) ? data.courses.filter(x=>x!==c) : [...data.courses,c]);
  const CLBL_D = buildCLBL(courseList);
  const safeId = initial?.id || uid();
  const save = () => {
    if (!data.name.trim()) return alert("Name required.");
    if (!data.location.trim()) return alert("Location required.");
    if (!data.courses.length) return alert("Select at least one course.");
    const imgs = (data.images||[]).filter(x=>x&&x.trim());
    const videos = (data.videos||[]).filter(v=>v.url&&v.url.trim()).map(v=>({...v, url:ytEmbed(v.url)||v.url}));
    onSave({ ...data, id:safeId, images:imgs.length?imgs:[PH], videos, courseLabel:data.courses.map(c=>CLBL_D[c]||c).join(" · "), contact:data.contact||{...BLANK_CONTACT} });
  };
  const contact = data.contact || {...BLANK_CONTACT};
  return (
    <div className="form-backdrop">
      <div className="form-overlay" onClick={onClose}/>
      <div className="form-box">
        <div className="form-head">
          <div><h3>{initial?.id?"Edit University":"Add University"}</h3><p>{initial?.id?`Editing: ${initial.name}`:"Fill in the details below"}</p></div>
          <button className="form-close" onClick={onClose}>✕</button>
        </div>
        <div className="form-body">
          <div className="f-section">Basic Info</div>
          <div className="form-row">
            <div className="f-group"><label className="f-label">Type</label><select className="f-select" value={data.type} onChange={e=>set("type",e.target.value)}><option value="abroad">🌍 Abroad</option><option value="domestic">🇮🇳 Domestic</option></select></div>
            {data.type==="domestic"
              ? <div className="f-group"><label className="f-label">Category</label><select className="f-select" value={data.category} onChange={e=>set("category",e.target.value)}><option value="primary">Primary</option><option value="secondary">Secondary</option><option value="premium">Premium</option></select></div>
              : <div className="f-group"><label className="f-label">Country</label><input className="f-input" placeholder="e.g. Georgia" value={data.country} onChange={e=>set("country",e.target.value)}/></div>
            }
          </div>
          <div className="f-group full"><label className="f-label">University Name *</label><input className="f-input" placeholder="Full name" value={data.name} onChange={e=>set("name",e.target.value)}/></div>
          <div className="form-row">
            <div className="f-group"><label className="f-label">Location *</label><input className="f-input" placeholder="City, Country" value={data.location} onChange={e=>set("location",e.target.value)}/></div>
            {data.type==="domestic" && <div className="f-group"><label className="f-label">State</label><select className="f-select" value={data.state} onChange={e=>set("state",e.target.value)}><option value="tamilnadu">Tamil Nadu</option><option value="karnataka">Karnataka</option><option value="kerala">Kerala</option></select></div>}
          </div>
          {data.type==="domestic" && <div className="f-group"><label className="f-label">City</label><input className="f-input" placeholder="e.g. Coimbatore" value={data.city} onChange={e=>set("city",e.target.value)}/></div>}
          <div className="f-divider"/>
          <div className="f-section">Courses & Availability</div>
          <div className="f-group full">
            <label className="f-label">Courses Offered *</label>
            <div className="courses-grid">
              {courseList.map(c => (<label className="course-check" key={c.value}><input type="checkbox" checked={data.courses.includes(c.value)} onChange={()=>toggle(c.value)}/><span>{c.label}</span></label>))}
            </div>
          </div>
          <div className="f-group"><label className="f-label">Overall Seats</label><input className="f-input" placeholder='e.g. 45 or "—"' value={data.seats} onChange={e=>set("seats",e.target.value)}/></div>
          <div className="f-divider"/>
          <div className="f-section">📞 Point of Contact</div>
          <div className="form-row">
            <div className="f-group"><label className="f-label">Contact Person Name</label><input className="f-input" placeholder="e.g. Mr. Rajesh Kumar" value={contact.name} onChange={e=>setContact("name",e.target.value)}/></div>
            <div className="f-group"><label className="f-label">Phone Number</label><input className="f-input" placeholder="+91 98765 43210" value={contact.phone} onChange={e=>setContact("phone",e.target.value)}/></div>
          </div>
          <div className="form-row">
            <div className="f-group"><label className="f-label">Email Address</label><input className="f-input" type="email" placeholder="admissions@uni.edu" value={contact.email} onChange={e=>setContact("email",e.target.value)}/></div>
            <div className="f-group"><label className="f-label">WhatsApp Number</label><input className="f-input" placeholder="+91 98765 43210" value={contact.whatsapp} onChange={e=>setContact("whatsapp",e.target.value)}/></div>
          </div>
          <div className="f-divider"/>
          <div className="f-section">Fee Structure</div>
          <FeeEditor courses={data.courses} feeStructures={data.feeStructures} onChange={fs=>set("feeStructures",fs)} uniType={data.type}/>
          <div className="f-divider"/>
          <div className="f-section">Campus Images</div>
          <div className="f-group full"><label className="f-label">Upload or paste image URLs</label><UploadField accept="image/*" multiple={true} label="campus images" icon="🖼️" hint="JPG, PNG, WebP — multiple allowed" value={data.images||[]} onChange={urls=>set("images",urls)}/></div>
          <div className="f-divider"/>
          <div className="f-section">🎬 Campus Videos</div>
          <div className="f-group full"><label className="f-label">Add videos (upload or YouTube)</label><VideosEditor videos={data.videos||[]} onChange={vids=>set("videos",vids)}/></div>
          <div className="f-divider"/>
          <div className="f-section">Visibility</div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 14px", background:data.hidden?"#fef9c3":"#f0fdf4", borderRadius:12, border:`1.5px solid ${data.hidden?"#fde047":"#86efac"}` }}>
            <div>
              <div style={{ fontFamily:"Syne,sans-serif", fontSize:12, fontWeight:800, color:data.hidden?"#a16207":"#15803d" }}>{data.hidden?"🙈 Hidden from portal":"👁 Visible in portal"}</div>
              <div style={{ fontSize:11, color:"var(--ink3)", marginTop:2 }}>{data.hidden?"Students cannot see this university":"Students can see this university"}</div>
            </div>
            <div onClick={()=>set("hidden",!data.hidden)} style={{ width:44, height:24, borderRadius:12, cursor:"pointer", background:data.hidden?"#fde047":"#16a34a", position:"relative", transition:"background .2s", flexShrink:0 }}>
              <div style={{ position:"absolute", top:3, left:data.hidden?3:23, width:18, height:18, borderRadius:"50%", background:"white", transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,.2)" }}/>
            </div>
          </div>
          <div className="f-divider"/>
          <div className="f-section">General Brochure</div>
          <div className="f-group full"><label className="f-label">Upload PDF or paste link</label><UploadField accept=".pdf" multiple={false} label="brochure PDF" icon="📄" hint="PDF only — main university brochure" value={data.brochure&&data.brochure!=="#"?data.brochure:""} onChange={url=>set("brochure",url||"#")}/></div>
          <div className="form-actions">
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button className="btn-save" onClick={save}>{initial?.id?"💾 Save Changes":"✚ Add University"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── INLINE COLOUR PICKER ────────────────────────────────────────────
function RowColorPicker({ course, onUpdate }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="color-picker-wrap">
      <div className="color-swatch" style={{ width:22, height:22, background:course.bg, borderColor:course.color }} onClick={()=>setOpen(p=>!p)}/>
      {open && <div className="color-dropdown">{TAG_PALETTE.map((p,i) => <div key={i} className="color-opt" style={{ background:p.bg }} onClick={()=>{onUpdate(p);setOpen(false);}}/>)}</div>}
    </div>
  );
}

// ─── COURSE MANAGER ──────────────────────────────────────────────────
function CourseManager({ courseList, onChange, showToast }) {
  const [newLabel, setNewLabel] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newColor, setNewColor] = useState(TAG_PALETTE[0]);
  const [showPalette, setShowPalette] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const slugify = s => s.toLowerCase().replace(/[^a-z0-9]+/g,"_").replace(/^_|_$/g,"");
  const addCourse = () => {
    const label = newLabel.trim(); if (!label) return alert("Course name required.");
    const value = newValue.trim() || slugify(label); if (!value) return alert("Invalid course key.");
    if (courseList.find(c=>c.value===value)) return alert(`Key "${value}" already exists.`);
    onChange([...courseList, { value, label, color:newColor.color, bg:newColor.bg }]);
    setNewLabel(""); setNewValue(""); showToast("Course added!");
  };
  const deleteCourse = c => {
    if (DEFAULT_COURSES.find(d=>d.value===c.value)) return alert("Default courses cannot be deleted.");
    onChange(courseList.filter(x=>x.value!==c.value)); showToast(`"${c.label}" removed.`,"error");
  };
  const saveEdit = c => {
    if (!editLabel.trim()) return;
    onChange(courseList.map(x=>x.value===c.value?{...x,label:editLabel.trim()}:x));
    setEditId(null); showToast("Course updated!");
  };
  return (
    <div className="course-manager">
      <div className="course-add-form">
        <div className="f-group"><label className="f-label">Course Name *</label><input className="f-input" placeholder="e.g. B.Com" value={newLabel} onChange={e=>setNewLabel(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addCourse()}/></div>
        <div className="f-group" style={{ maxWidth:150 }}><label className="f-label">Key (auto)</label><input className="f-input" placeholder="e.g. bcom" value={newValue} onChange={e=>setNewValue(e.target.value)}/></div>
        <div className="f-group" style={{ maxWidth:80 }}>
          <label className="f-label">Colour</label>
          <div className="color-picker-wrap">
            <div className="color-swatch" style={{ background:newColor.bg, borderColor:newColor.color }} onClick={()=>setShowPalette(p=>!p)}/>
            {showPalette && <div className="color-dropdown">{TAG_PALETTE.map((p,i)=><div key={i} className="color-opt" style={{ background:p.bg }} onClick={()=>{setNewColor(p);setShowPalette(false);}}/>)}</div>}
          </div>
        </div>
        <button className="btn-add-course" onClick={addCourse}>+ Add Course</button>
      </div>
      <div className="courses-list">
        {courseList.map(c => {
          const isDefault = !!DEFAULT_COURSES.find(d=>d.value===c.value);
          return (
            <div className="course-item" key={c.value}>
              <div className="course-color-dot" style={{ background:c.bg, color:c.color }}>{c.label.charAt(0)}</div>
              {editId===c.value ? (
                <div className="course-inline-edit">
                  <input value={editLabel} onChange={e=>setEditLabel(e.target.value)} autoFocus onKeyDown={e=>{if(e.key==="Enter")saveEdit(c);if(e.key==="Escape")setEditId(null);}}/>
                  <button className="btn-save-course" onClick={()=>saveEdit(c)}>✓</button>
                  <button className="btn-cancel-course" onClick={()=>setEditId(null)}>✕</button>
                </div>
              ) : (
                <div className="course-item-info"><div className="course-item-label">{c.label}</div><div className="course-item-value">key: {c.value}</div></div>
              )}
              {editId!==c.value && (
                <div className="course-item-acts">
                  {!isDefault && <RowColorPicker course={c} onUpdate={p=>onChange(courseList.map(x=>x.value===c.value?{...x,color:p.color,bg:p.bg}:x))}/>}
                  {isDefault ? <span className="course-item-default">Default</span>
                    : <><button className="btn-sm btn-edit" onClick={()=>{setEditId(c.value);setEditLabel(c.label);}}>Edit</button><button className="btn-sm btn-del" onClick={()=>deleteCourse(c)}>Del</button></>
                  }
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ADMIN LOGIN ──────────────────────────────────────────────────────
function AdminLogin({ onAuth }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-icon">🔐</div>
        <h2 className="login-title">Admin Access</h2>
        <p className="login-sub">Enter password to manage universities</p>
        <form className="login-form" onSubmit={e=>{e.preventDefault();if(pw===ADMIN_PASSWORD){onAuth();}else{setErr(true);setPw("");}}}>
          {err && <div className="login-err">Incorrect password.</div>}
          <input className="f-input" type="password" placeholder="Password" value={pw} onChange={e=>{setPw(e.target.value);setErr(false);}} autoFocus/>
          <button className="btn-login" type="submit">Unlock Admin →</button>
        </form>
      </div>
    </div>
  );
}

// ─── ADMIN TABLE ──────────────────────────────────────────────────────
function AdminTable({ universities, courseList, onAdd, onEdit, onDelete, onToggleHide, onCourseChange, showToast }) {
  const [tab, setTab] = useState("universities");
  const abroad = universities.filter(u=>u.type==="abroad");
  const domestic = universities.filter(u=>u.type==="domestic");
  const CLBL_D = buildCLBL(courseList);
  const validCourseValues = new Set(courseList.map(c=>c.value));

  const Row = ({ u }) => {
    const validCourses = u.courses.filter(c=>validCourseValues.has(c));
    return (
      <tr style={u.hidden?{opacity:.6,background:"#fafafa"}:{}}>
        <td style={{ position:"relative" }}>
          <img className="td-img" src={u.images?.[0]||PH} alt="" onError={e=>{e.target.src=PH;}} style={u.hidden?{filter:"grayscale(1)"}:{}}/>
          {u.hidden && <span style={{ position:"absolute", top:14, left:14, background:"#374151", color:"white", fontSize:8, fontWeight:800, padding:"1px 5px", borderRadius:4 }}>HIDDEN</span>}
        </td>
        <td className="td-name" style={u.hidden?{color:"var(--ink3)"}:{}}>{u.name}</td>
        <td className="td-loc">{u.location}</td>
        <td><span className={`type-badge ${u.type==="abroad"?"type-abroad":"type-domestic"}`}>{u.type==="abroad"?"🌍":"🇮🇳"} {u.type}</span></td>
        <td>{u.category && <span className={`tag ${CAT_CLASS[u.category]}`}>{CAT_LBL[u.category]}</span>}</td>
        <td><div className="td-tags">{validCourses.slice(0,3).map(c=><span key={c} className="tag" style={getCourseStyle(courseList,c)}>{CLBL_D[c]||c}</span>)}{validCourses.length>3&&<span className="tag" style={{background:"#f1f5f9",color:"#475569"}}>+{validCourses.length-3}</span>}</div></td>
        <td style={{ fontSize:11 }}>{(u.contact?.name||u.contact?.phone)?<span style={{color:"var(--green)",fontWeight:700}}>✓</span>:<span style={{color:"var(--ink3)"}}>—</span>}</td>
        <td><div className="td-acts">
          <button className={`btn-sm ${u.hidden?"btn-hide-off":"btn-hide-on"}`} onClick={()=>onToggleHide(u)}>{u.hidden?"👁 Show":"🙈 Hide"}</button>
          <button className="btn-sm btn-edit" onClick={()=>onEdit(u)}>Edit</button>
          <button className="btn-sm btn-del" onClick={()=>onDelete(u)}>Del</button>
        </div></td>
      </tr>
    );
  };

  const Sec = ({ title, data }) => (<>
    <tr className="section-row"><td colSpan={8}>{title} ({data.length})</td></tr>
    {data.length===0 ? <tr><td colSpan={8} style={{padding:"16px 14px",color:"var(--ink3)",textAlign:"center",fontSize:12}}>No entries yet</td></tr> : data.map(u=><Row key={u.id} u={u}/>)}
  </>);

  return (
    <div className="admin-panel">
      <div className="admin-head" style={{ paddingBottom:0 }}>
        <div><h2>🎓 University Manager</h2><p>{universities.length} universities · {courseList.length} courses</p></div>
        {tab==="universities" && <button className="admin-add" onClick={onAdd}>+ Add University</button>}
      </div>
      <div className="admin-tabs">
        <button className={`admin-tab-btn${tab==="universities"?" active":""}`} onClick={()=>setTab("universities")}>🏫 Universities</button>
        <button className={`admin-tab-btn${tab==="courses"?" active":""}`} onClick={()=>setTab("courses")}>📚 Courses</button>
      </div>
      {tab==="universities" && <div className="table-wrap"><table><thead><tr><th>IMG</th><th>NAME</th><th>LOCATION</th><th>TYPE</th><th>CAT</th><th>COURSES</th><th>CONTACT</th><th>ACTIONS</th></tr></thead><tbody><Sec title="Abroad" data={abroad}/><Sec title="Domestic" data={domestic}/></tbody></table></div>}
      {tab==="courses" && <CourseManager courseList={courseList} onChange={onCourseChange} showToast={showToast}/>}
    </div>
  );
}

// ─── CARD ─────────────────────────────────────────────────────────────
function UniCard({ u, isAdmin, courseList, onViewDetails, onEdit, onDelete, onToggleHide }) {
  const CLBL_D = buildCLBL(courseList);
  const validCourseValues = new Set(courseList.map(c=>c.value));
  const validCourses = u.courses.filter(c=>validCourseValues.has(c));
  const locBadge = u.type==="abroad"?(u.country||u.location):(u.state==="karnataka"?"Karnataka":"Tamil Nadu");
  const seats = u.seats&&u.seats!=="—"?u.seats:"Enquire";
  return (
    <div className={`card${u.hidden?" is-hidden":""}`}>
      <div className="card-img">
        <img src={u.images?.[0]||PH} alt={u.name} loading="lazy" onError={e=>{e.target.src=PH;}}/>
        <div className="card-badge">{locBadge}</div>
        <div className="seats-badge"><span className="seats-dot"/>{seats}</div>
        {u.hidden && isAdmin && <div className="hidden-overlay"><div className="hidden-badge-card">🙈 Hidden from portal</div></div>}
      </div>
      <div className="card-body">
        <div className="card-tags">
          {u.category && <span className={`tag ${CAT_CLASS[u.category]}`}>{CAT_LBL[u.category]}</span>}
          {validCourses.slice(0,2).map(c=><span key={c} className="tag" style={getCourseStyle(courseList,c)}>{CLBL_D[c]||c}</span>)}
          {validCourses.length>2 && <span className="tag" style={{background:"#f1f5f9",color:"#475569"}}>+{validCourses.length-2}</span>}
        </div>
        <div className="card-name">{u.name}</div>
        <div className="card-loc"><svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>{u.location}</div>
        <div className="card-actions">
          <button className="btn-details" onClick={()=>onViewDetails(u)}>View Details →</button>
          {isAdmin && <>
            <button className="btn-icon" onClick={()=>onToggleHide(u)} style={u.hidden?{background:"#f3e8ff",borderColor:"#d8b4fe"}:{}}>{u.hidden?"👁":"🙈"}</button>
            <button className="btn-icon" onClick={()=>onEdit(u)}>✏️</button>
            <button className="btn-icon del" onClick={()=>onDelete(u)}>🗑️</button>
          </>}
        </div>
      </div>
    </div>
  );
}

// ─── DETAIL MODAL ─────────────────────────────────────────────────────
function DetailModal({ u, courseList, onClose }) {
  const [imgIdx, setImgIdx] = useState(0);
  const [videoIdx, setVideoIdx] = useState(0);
  const CLBL_D = buildCLBL(courseList);
  const validCourseValues = new Set(courseList.map(c=>c.value));
  const validCourses = u.courses.filter(c=>validCourseValues.has(c));
  const imgs = u.images?.length ? u.images : [PH];
  const normalized = normalizeVideos(u);
  const videos = (normalized.videos||[]).filter(v=>v.url&&v.url.trim());
  const sd = (!u.seats||u.seats==="—")?{v:"Contact us",l:"for availability"}:{v:u.seats,l:"seats remaining"};
  const contact = u.contact || {};
  const hasWhatsApp = !!(contact.whatsapp&&contact.whatsapp.trim());
  const validCourseLabel = validCourses.map(c=>CLBL_D[c]||c).join(" · ")||u.courseLabel||"—";

  useEffect(() => { const h=e=>{if(e.key==="Escape")onClose();}; window.addEventListener("keydown",h); return ()=>window.removeEventListener("keydown",h); }, [onClose]);

  const activeVideo = videos[videoIdx];
  const renderVideoPlayer = (v) => {
    if (!v) return null;
    const url = v.url;
    const isYt = url.includes("youtube.com/embed")||url.includes("youtu.be")||url.includes("youtube.com/watch");
    if (isYt) { const embedUrl = url.includes("embed")?url:ytEmbed(url); return <iframe src={embedUrl} frameBorder="0" allowFullScreen title={v.label||"video"} style={{ width:"100%", aspectRatio:"16/9", borderRadius:8, display:"block", border:"none" }}/>; }
    return <video src={url} controls style={{ width:"100%", borderRadius:8, display:"block", maxHeight:220 }} preload="metadata"/>;
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-overlay" onClick={onClose}/>
      <div className="modal-box">
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-media">
          <div className="carousel">
            <img src={imgs[imgIdx]} alt="" onError={e=>{e.target.src=PH;}}/>
            {imgs.length>1 && <>
              <button className="car-btn prev" onClick={()=>setImgIdx((imgIdx-1+imgs.length)%imgs.length)}>‹</button>
              <button className="car-btn next" onClick={()=>setImgIdx((imgIdx+1)%imgs.length)}>›</button>
              <div className="car-dots">{imgs.map((_,i)=><button key={i} className={`cdot${i===imgIdx?" active":""}`} onClick={()=>setImgIdx(i)}/>)}</div>
            </>}
          </div>
          {videos.length>0 && (
            <div className="modal-videos">
              {videos.length>1 && <div className="modal-video-tabs">{videos.map((v,i)=><button key={i} className={`modal-video-tab${i===videoIdx?" active":""}`} onClick={()=>setVideoIdx(i)}>🎬 {v.label||`Video ${i+1}`}</button>)}</div>}
              <div className="modal-video-player">{renderVideoPlayer(activeVideo)}</div>
            </div>
          )}
        </div>
        <div className="modal-info">
          <div className="modal-tags">
            {validCourses.slice(0,4).map(c=><span key={c} className="tag" style={getCourseStyle(courseList,c)}>{CLBL_D[c]||c}</span>)}
            {u.category && <span className={`tag ${CAT_CLASS[u.category]}`}>{CAT_LBL[u.category]}</span>}
          </div>
          <h2 className="modal-title">{u.name}</h2>
          <div className="info-rows">
            <div className="info-row"><div className="info-icon loc">📍</div><div><div className="info-lbl">Location</div><div className="info-val">{u.location}</div></div></div>
            <div className="info-row"><div className="info-icon course">🎓</div><div><div className="info-lbl">Programs</div><div className="info-val">{validCourseLabel}</div></div></div>
            <div className="info-row"><div className="info-icon seats">⚡</div><div><div className="info-lbl">Availability</div><div className="info-val"><span className="seats-val">{sd.v}</span> {sd.l}</div></div></div>
          </div>
          <ContactDisplay contact={u.contact}/>
          <FeeDisplay feeStructures={(u.feeStructures||[]).filter(f=>validCourseValues.has(f.course))} uniType={u.type}/>
          <div className="modal-ctas">
            {hasWhatsApp && <a href={`https://wa.me/${contact.whatsapp.replace(/[^0-9]/g,"")}?text=Hi, I'm interested in ${encodeURIComponent(u.name)}. Could you please share more details?`} target="_blank" rel="noreferrer" className="cta cta-wa">💬 WhatsApp Us</a>}
            {contact.phone && <a href={`tel:${contact.phone.replace(/\s/g,"")}`} className="cta cta-s">📱 Call {contact.name||"Now"}</a>}
            {u.brochure&&u.brochure!=="#" && <a href={u.brochure} target="_blank" rel="noreferrer" className="cta cta-p">📄 Download Brochure</a>}
            {(!u.brochure||u.brochure==="#")&&!contact.phone&&!hasWhatsApp && <a href="#" className="cta cta-p">📄 Download Brochure</a>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FILTER CONTENT ───────────────────────────────────────────────────
function FilterContent({ courseList, activeTab, search, setSearch, selCourses, toggleCourse, selCats, toggleCat, selState, setSelState, selCity, setSelCity, resetFilters }) {
  const cities = {
    tamilnadu:["coimbatore","chennai","madurai","trichy","salem","tirunelveli","vellore","erode","thoothukudi","tiruppur","dindigul","karur","namakkal","kumbakonam","cuddalore","nagapattinam","hosur","nagercoil","thanjavur"],
    karnataka:["bangalore","mangalore","mysore","hubli","dharwad","belgaum","shimoga","tumkur","udupi","davangere","bellary","bijapur","gulbarga","raichur","hassan"],
    kerala:["kochi","trivandrum","kozhikode","thrissur","kollam","alappuzha","palakkad","kottayam","kannur","malappuram","kasaragod"]
  };
  return (
    <>
      <div className="filter-section">
        <div className="filter-section">
          <div className="filter-label">Category</div>
          <div className="cat-chips">
            {["primary","secondary","premium"].map(cat=><button key={cat} className={`cat-chip${selCats.includes(cat)?` active-${cat}`:""}`} onClick={()=>toggleCat(cat)}>{CAT_LBL[cat]}</button>)}
          </div>
        </div>
        <div className="filter-label">Search</div>
        <div className="search-wrap">
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input type="text" placeholder="University name…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
      </div>
      <div className="s-divider"/>
      <div className="filter-section">
        <div className="filter-label">Courses <span className="counter-pill">{selCourses.length}</span></div>
        <div className="check-list">
          {courseList.map(c=><label className="check-label" key={c.value}><input type="checkbox" checked={selCourses.includes(c.value)} onChange={()=>toggleCourse(c.value)}/><span>{c.label}</span></label>)}
        </div>
      </div>
      {activeTab==="domestic" && <>
        <div className="s-divider"/>
        <div className="filter-section">
          <div className="filter-label">State</div>
          <select className="sidebar-select" value={selState} onChange={e=>{setSelState(e.target.value);setSelCity("all");}}>
            <option value="all">All States</option><option value="karnataka">Karnataka</option><option value="tamilnadu">Tamil Nadu</option><option value="kerala">Kerala</option>
          </select>
        </div>
        <div className="filter-section">
          <div className="filter-label">City</div>
          <select className="sidebar-select" value={selCity} onChange={e=>setSelCity(e.target.value)}>
            <option value="all">All Cities</option>
            {(cities[selState]||[]).map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
          </select>
        </div>
      </>}
      <div className="s-divider"/>
      <button className="reset-btn" onClick={resetFilters}>↺ Reset All Filters</button>
    </>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────
export default function App() {
  // ── Use localStorage so auth persists across browser restarts ──
  const [portalAuth, setPortalAuth] = useState(() => localStorage.getItem("up_auth") === "1");
  const [universities, setUniversities] = useState([]);
  const [courseList, setCourseList] = useState(DEFAULT_COURSES);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("abroad");
  const [view, setView] = useState("portal");
  const [adminAuth, setAdminAuth] = useState(() => localStorage.getItem("up_admin") === "1");

  const [search, setSearch] = useState("");
  const [selCourses, setSelCourses] = useState(DEFAULT_COURSES.map(c=>c.value));
  const [selCats, setSelCats] = useState(["primary","secondary","premium"]);
  const [selState, setSelState] = useState("all");
  const [selCity, setSelCity] = useState("all");

  const [detailUni, setDetailUni] = useState(null);
  const [formUni, setFormUni] = useState(null);
  const [deleteUni, setDeleteUni] = useState(null);
  const [toast, setToast] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const showToast = useCallback((msg, type="success") => setToast({msg, type}), []);

  // ── Load data from PHP API on mount ──────────────────────────────
  useEffect(() => {
    async function load() {
      try {
        const data = await apiGet();
        if (data.universities && data.universities.length > 0) {
          setUniversities(data.universities.map(normalizeVideos));
          if (data.courses && data.courses.length > 0) {
            setCourseList(data.courses);
            setSelCourses(data.courses.map(c=>c.value));
          }
          setLoaded(true);
        } else {
          // First time — seed default data
          await apiSeed(
            [...DEFAULT_ABROAD, ...DEFAULT_DOMESTIC].map(normalizeVideos),
            DEFAULT_COURSES
          );
          setUniversities([...DEFAULT_ABROAD, ...DEFAULT_DOMESTIC].map(normalizeVideos));
          setCourseList(DEFAULT_COURSES);
          setLoaded(true);
        }
      } catch(err) {
        console.error("Load error:", err);
        // Fallback to defaults if API unreachable
        setUniversities([...DEFAULT_ABROAD, ...DEFAULT_DOMESTIC].map(normalizeVideos));
        setCourseList(DEFAULT_COURSES);
        setLoaded(true);
        showToast("Could not connect to server — showing defaults", "error");
      }
    }
    load();
  }, [showToast]);

  const handleCourseChange = useCallback(async (updated) => {
    setCourseList(updated);
    setSelCourses(prev => {
      const newVals = updated.map(c=>c.value);
      const kept = prev.filter(v=>newVals.includes(v));
      const added = newVals.filter(v=>!prev.includes(v));
      return [...kept, ...added];
    });
    try { await apiSaveCourses(updated); }
    catch(e) { showToast("Failed to save courses", "error"); console.error(e); }
  }, [showToast]);

  const handleSave = async (data) => {
    const isEdit = universities.some(u=>u.id===data.id);
    try {
      await apiSaveUniversity(data);
      setUniversities(prev => {
        if (isEdit) return prev.map(u=>u.id===data.id?data:u);
        return [...prev, data];
      });
      showToast(isEdit?"Updated!":"Added!");
    } catch(e) {
      showToast("Save failed. Check server.", "error");
      console.error(e);
    }
    setFormUni(null);
  };

  const handleToggleHide = async (u) => {
    const updated = {...u, hidden:!u.hidden};
    try {
      await apiSaveUniversity(updated);
      setUniversities(prev=>prev.map(x=>x.id===u.id?updated:x));
      showToast(updated.hidden?`"${u.name}" hidden.`:`"${u.name}" is now visible.`, updated.hidden?"error":"success");
    } catch(e) { showToast("Failed to update visibility.", "error"); console.error(e); }
  };

  const handleDelete = async (u) => {
    try {
      await apiDeleteUniversity(u.id);
      setUniversities(prev=>prev.filter(x=>x.id!==u.id));
      showToast(`"${u.name}" deleted.`, "error");
    } catch(e) { showToast("Delete failed.", "error"); console.error(e); }
    setDeleteUni(null);
  };

  const toggleCourse = c => setSelCourses(p=>p.includes(c)?p.filter(x=>x!==c):[...p,c]);
  const toggleCat = c => setSelCats(p=>p.includes(c)?p.filter(x=>x!==c):[...p,c]);
  const resetFilters = () => { setSearch(""); setSelState("all"); setSelCity("all"); setSelCourses(courseList.map(c=>c.value)); setSelCats(["primary","secondary","premium"]); };

  const visible = universities.filter(u => {
    if (u.type !== activeTab) return false;
    if (!adminAuth && u.hidden) return false;
    if (search && !u.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (!u.courses.some(c=>selCourses.includes(c))) return false;
    if (activeTab==="domestic") {
      if (selState!=="all" && u.state!==selState) return false;
      if (selCity!=="all" && u.city!==selCity) return false;
      if (!selCats.includes(u.category)) return false;
    }
    return true;
  });

  const filterCount = (courseList.length-selCourses.length)+(3-selCats.length)+(search?1:0)+(selState!=="all"?1:0)+(selCity!=="all"?1:0);
  const filterProps = { courseList, activeTab, search, setSearch, selCourses, toggleCourse, selCats, toggleCat, selState, setSelState, selCity, setSelCity, resetFilters };

  if (!portalAuth) {
    return <PortalLogin onAuth={(role) => {
      localStorage.setItem("up_auth","1");
      setPortalAuth(true);
      if (role==="admin") { localStorage.setItem("up_admin","1"); setAdminAuth(true); setView("admin"); }
    }}/>;
  }

  if (!loaded) return (
    <>
      <style>{STYLES}</style>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", fontFamily:"Syne,sans-serif", fontSize:17, color:"var(--ink3)" }}>Loading…</div>
    </>
  );

  return (
    <>
      <style>{STYLES}</style>
      <header className="header">
        <div className="logo" onClick={()=>setView("portal")}>
          <div className="logo-mark">U</div>
          <span className="logo-text">UNI<span className="logo-accent">PORTAL</span></span>
        </div>
        {view==="portal" && (
          <div className="tab-group">
            <button className={`tab-btn${activeTab==="abroad"?" active":""}`} onClick={()=>setActiveTab("abroad")}>🌍 Abroad</button>
            <button className={`tab-btn${activeTab==="domestic"?" active":""}`} onClick={()=>setActiveTab("domestic")}>🇮🇳 Domestic</button>
          </div>
        )}
        <div className="header-right">
          {view==="portal" && <button className={`filter-toggle-btn${filterCount>0?" has-filters":""}`} onClick={()=>setDrawerOpen(true)}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>Filters{filterCount>0&&<span className="filter-badge">{filterCount}</span>}</button>}
          <button className={`admin-btn${view==="admin"?" active-admin":""}`} onClick={()=>{if(view==="admin")setView("portal");else if(adminAuth)setView("admin");else setView("admin-login");}}>
            {view==="admin"?"← Portal":"⚙️ Admin"}
          </button>
          <button className="admin-btn" title="Sign out" onClick={()=>{localStorage.removeItem("up_auth");localStorage.removeItem("up_admin");setPortalAuth(false);setAdminAuth(false);setView("portal");}}>➜]</button>
        </div>
      </header>

      {view==="admin-login" && <AdminLogin onAuth={()=>{setAdminAuth(true);setView("admin");}}/>}
      {view==="admin" && <AdminTable universities={universities} courseList={courseList} onAdd={()=>setFormUni({})} onEdit={u=>setFormUni(u)} onDelete={u=>setDeleteUni(u)} onToggleHide={handleToggleHide} onCourseChange={handleCourseChange} showToast={showToast}/>}

      {view==="portal" && (
        <div className="layout">
          <aside className="sidebar-wrap"><div className="sidebar-inner"><FilterContent {...filterProps}/></div></aside>
          <main>
            <div className="stats-bar">
              <h2>Universities <span className="stats-count">({visible.length})</span></h2>
              {adminAuth && <button className="add-btn-top" onClick={()=>setFormUni({})}>+ Add</button>}
            </div>
            <div className="card-grid">
              {visible.map(u=><UniCard key={u.id} u={u} isAdmin={adminAuth} courseList={courseList} onViewDetails={setDetailUni} onEdit={u=>setFormUni(u)} onDelete={u=>setDeleteUni(u)} onToggleHide={handleToggleHide}/>)}
              {visible.length===0 && <div className="no-results"><div className="no-results-icon">🏜️</div><h3>No Universities Found</h3><p>Try adjusting filters or search.</p></div>}
            </div>
          </main>
        </div>
      )}

      <nav className="bottom-tabs">
        <button className={`bottom-tab${activeTab==="abroad"&&view==="portal"?" active":""}`} onClick={()=>{setActiveTab("abroad");setView("portal");}}>
          <span className="bottom-tab-icon">🌍</span><span>Abroad</span><span className="bottom-tab-dot"/>
        </button>
        <button className={`bottom-tab${activeTab==="domestic"&&view==="portal"?" active":""}`} onClick={()=>{setActiveTab("domestic");setView("portal");}}>
          <span className="bottom-tab-icon">🇮🇳</span><span>Domestic</span><span className="bottom-tab-dot"/>
        </button>
        <button className={`bottom-tab${view==="admin"||view==="admin-login"?" active":""}`} onClick={()=>{if(view==="admin")setView("portal");else if(adminAuth)setView("admin");else setView("admin-login");}}>
          <span className="bottom-tab-icon">⚙️</span><span>Admin</span><span className="bottom-tab-dot"/>
        </button>
      </nav>

      <div className={`drawer-backdrop${drawerOpen?" open":""}`} onClick={()=>setDrawerOpen(false)}/>
      <div className={`drawer${drawerOpen?" open":""}`}>
        <div className="drawer-handle"/>
        <div className="drawer-head"><h3>Filter Universities</h3><button className="drawer-close" onClick={()=>setDrawerOpen(false)}>✕</button></div>
        <div className="drawer-body"><FilterContent {...filterProps}/><button className="drawer-apply" onClick={()=>setDrawerOpen(false)}>Show {visible.length} Results →</button></div>
      </div>

      {detailUni && <DetailModal u={detailUni} courseList={courseList} onClose={()=>setDetailUni(null)}/>}
      {formUni!==null && <FormModal initial={formUni?.id?formUni:null} courseList={courseList} onSave={handleSave} onClose={()=>setFormUni(null)}/>}
      {deleteUni && <ConfirmDialog name={deleteUni.name} onConfirm={()=>handleDelete(deleteUni)} onCancel={()=>setDeleteUni(null)}/>}
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={()=>setToast(null)}/>}
    </>
  );
}